import { useMemo, useState, useEffect, useRef } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { parseInboxParam, resolveInboxTargetKey, type InboxTarget } from "@/lib/team-inbox-deeplink";
import AppLayout, { findOpenThermostatStuckSince, formatStuckDuration, resolveThermostatRoute } from "@/components/app-layout";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { useFeatureGates, navHiddenFor } from "@/hooks/use-feature-gates";
import { useHomeCardVisibility } from "@/hooks/use-home-card-visibility";
import { HOME_PANEL_HREFS } from "@/lib/home-card-visibility";
import { isDraftMessage } from "@/lib/messages-tabs";
import {
  Building2,
  DoorOpen,
  CalendarClock,
  AlertTriangle,
  CheckCircle2,
  Check,
  Bell,
  Thermometer,
  Wrench,
  CreditCard,
  MessageSquare,
  Handshake,
  DollarSign,
  Hammer,
  RefreshCw,
  Wifi,
  WifiOff,
  ArrowRight,
  Lock,
  BatteryWarning,
  ShoppingCart,
  LayoutGrid,
  GripVertical,
  X,
  Save,
  Columns,
  Rows,
  Square,
  Minimize2,
  Hourglass,
  RotateCcw,
  Target,
  FileText,
  Clock,
  CheckCircle,
  Compass,
  EyeOff,
  Eye,
  PlusCircle,
  Image as ImageIcon,
  Video,
  Mic,
  Paperclip,
  Users,
} from "lucide-react";
import { HOST_MAX_ACTIVE_PROPERTIES, HOST_MAX_ROOMS_PER_PROPERTY, hasFeatureAccess, JOURNEY_STAGE_LABELS, ASSIGNMENT_NOTIFICATION_TYPES, resolveAssignmentNotificationPrefs, type AccountType, type JourneyStage } from "@shared/schema";
import { usePushSubscription } from "@/hooks/use-push-subscription";
import { useFirstVisitPushPrompt } from "@/hooks/use-first-visit-push-prompt";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { format, differenceInDays, formatDistanceToNow } from "date-fns";
import type { Notification as AppNotification, MaintenanceTask, Property, RenovationItem, Acquisition, PanelSize, SupplyOrder, TeamMember, Room as SchemaRoom, LockBatteryNotification } from "@shared/schema";
import { ACQUISITION_STATUSES } from "@shared/schema";
import { PropertyGroupFilter } from "@/components/property-group-filter";
import { TwoFactorNudgeBanner } from "@/components/two-factor-nudge-banner";
import { getGroupFilter, setGroupFilter } from "@/lib/group-filter-state";
import { computeOccupancy } from "@shared/occupancy";
import { computePnlProfitForMonth } from "@/lib/pnl-profit";
import { formatInboxPreview, inboxPreviewMediaType } from "@/lib/inbox-media";
import { consumeOnboardingJustCompleted } from "@/lib/onboarding-signal";
import { invalidateResolveQueries } from "@/pages/app-messages/_resolve-invalidations";

interface SeamDevice {
  device_id: string;
  display_name: string;
  device_type: string;
  properties?: {
    online?: boolean;
    battery?: { level?: number };
    locked?: boolean;
    temperature?: number;
    // Fahrenheit reading and setpoints surfaced directly by Seam. Used by
    // the dashboard health panel to detect when the actual temperature is
    // more than 2°F outside the active setpoint range.
    temperature_fahrenheit?: number;
    relative_humidity?: number;
    cooling_set_point_fahrenheit?: number;
    heating_set_point_fahrenheit?: number;
    current_climate_setting?: {
      hvac_mode_setting?: string;
      cooling_set_point_fahrenheit?: number;
      heating_set_point_fahrenheit?: number;
    };
    has_direct_power?: boolean;
  };
  location?: { location_name?: string };
  // Task #2325 — PadPulse-augmented fields added by GET /api/app/devices/seam/locks.
  // `__padpulse_unassigned` is true when the device has no active pin and no
  // name-match against any owned property, i.e. the lock is not assigned to
  // any property and should be excluded from health alerts.
  __padpulse_unassigned?: boolean;
}

// Default for properties without an explicit per-property override. Mirrors
// DEFAULT_HEALTH_THRESHOLD_F on the server-side poller so the dashboard's
// at-a-glance out-of-range badge agrees with what the cron job will alert on.
const DEFAULT_THERMOSTAT_HEALTH_THRESHOLD_F = 2;

// Mirror of the server-side `evaluateThermostatHealth` rule so the dashboard
// can show an at-a-glance out-of-range badge without waiting for the next
// poll cycle to materialize a notification.
function evaluateOutOfRange(
  d: SeamDevice,
  thresholdF: number = DEFAULT_THERMOSTAT_HEALTH_THRESHOLD_F,
): { kind: "above_cool" | "below_heat"; deltaF: number; setF: number; tempF: number; thresholdF: number } | null {
  const props = d.properties;
  if (!props) return null;
  const tempF = props.temperature_fahrenheit;
  if (typeof tempF !== "number" || Number.isNaN(tempF)) return null;
  const climate = props.current_climate_setting ?? {};
  const coolF = climate.cooling_set_point_fahrenheit ?? props.cooling_set_point_fahrenheit;
  const heatF = climate.heating_set_point_fahrenheit ?? props.heating_set_point_fahrenheit;
  const mode = (climate.hvac_mode_setting || "").toLowerCase();
  const allowAboveCool = !mode || mode === "cool" || mode === "auto" || mode === "heat_cool";
  const allowBelowHeat = !mode || mode === "heat" || mode === "auto" || mode === "heat_cool";
  if (allowAboveCool && typeof coolF === "number" && tempF - coolF > thresholdF) {
    return { kind: "above_cool", deltaF: tempF - coolF, setF: coolF, tempF, thresholdF };
  }
  if (allowBelowHeat && typeof heatF === "number" && heatF - tempF > thresholdF) {
    return { kind: "below_heat", deltaF: heatF - tempF, setF: heatF, tempF, thresholdF };
  }
  return null;
}

interface SeamConnection {
  id: number;
  provider: string;
  accountEmail?: string;
  deviceType?: string;
}

interface OperationsApproval {
  id: number;
  title: string;
  type: string;
  status: string;
  priceCents?: number | null;
  vendorId?: number | null;
  propertyId?: number | null;
}

interface ActionableMessage {
  id: number;
  conversationId: number;
  content: string;
  senderName: string | null;
  assigneeName: string | null;
  dueDate: string | null;
  createdAt: string;
  memberName: string;
}


interface PadsplitFinancial {
  id: number;
  propertyId?: number;
  periodStart?: string | null;
  periodEnd?: string | null;
  totalEarnings?: string | null;
  managementFees?: string | null;
  netPayout?: string | null;
  otherFees?: string | null;
}

interface Room {
  id: number;
  propertyId: number;
  status: string;
  roomNumber: string;
  propertyStatus?: string;
}

interface MemberSummary {
  id: number;
  propertyId: number;
  status: string;
}

interface Vendor {
  id: number;
  name: string;
}

function fmtCurrency(val: number): string {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 });
}

function PanelHeader({ title, href, icon: Icon, count }: { title: string; href: string; icon: typeof Building2; count?: number }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-gray-500" />
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {count != null && count > 0 && (
          <span className="inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none px-1.5 py-0.5 min-w-[18px]" data-testid={`badge-count-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            {count}
          </span>
        )}
      </div>
      <Link href={href}>
        <span className="text-xs text-brand hover:underline flex items-center gap-0.5 cursor-pointer" data-testid={`link-panel-${title.toLowerCase().replace(/\s+/g, "-")}`}>
          View all <ArrowRight className="h-3 w-3" />
        </span>
      </Link>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-6 text-center text-sm text-gray-400" data-testid="text-empty-state">{message}</div>
  );
}

export type MetricState = "neutral" | "red" | "yellow" | "green";

export const METRIC_STATE_STYLES: Record<MetricState, { card: string; icon: string; value: string }> = {
  neutral: { card: "border-gray-200 bg-white", icon: "text-gray-400", value: "text-gray-900" },
  red: { card: "border-red-200 bg-red-50", icon: "text-red-500", value: "text-red-600" },
  yellow: { card: "border-yellow-200 bg-yellow-50", icon: "text-yellow-600", value: "text-yellow-700" },
  green: { card: "border-green-200 bg-green-50", icon: "text-green-600", value: "text-green-700" },
};

// Occupancy color thresholds: below 80% is bad (red), 80–90% inclusive is a
// warning (yellow), above 90% is good (green).
export function occupancyState(occupancyPct: number): MetricState {
  return occupancyPct < 80 ? "red" : occupancyPct <= 90 ? "yellow" : "green";
}

// Last Month's Profit color: a loss (below zero) is red, break-even or a profit
// (zero or above) is green.
export function profitState(profit: number): MetricState {
  return profit < 0 ? "red" : "green";
}

// The Open Tasks card only surfaces when at least one still-open task is past
// its due date. `now` is injectable so tests stay deterministic.
export function hasOverdueTask(
  openTasks: ReadonlyArray<{ dueDate?: string | Date | null }>,
  now: Date = new Date(),
): boolean {
  return openTasks.some((t) => t.dueDate != null && new Date(t.dueDate) < now);
}

export function MetricCard({ label, value, state = "neutral", icon: Icon, href, inFlight, caption }: {
  label: string;
  value: number | string;
  state?: MetricState;
  icon: typeof Building2;
  href: string;
  inFlight?: { tooltip: string };
  caption?: string;
}) {
  const testId = `metric-card-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const styles = METRIC_STATE_STYLES[state];
  return (
    <Link href={href}>
      <div
        className={`h-full border rounded-lg p-4 cursor-pointer hover:shadow-sm transition-shadow flex flex-col gap-1 ${styles.card}`}
        data-testid={testId}
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{label}</span>
          <Icon className={`h-4 w-4 ${styles.icon}`} />
        </div>
        <span className={`text-2xl font-bold ${styles.value}`}>{value}</span>
        {caption && <span className="text-xs text-gray-500">{caption}</span>}
        {inFlight && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 font-medium leading-none w-fit cursor-default"
                  onClick={(e) => e.preventDefault()}
                  data-testid={`${testId}-in-flight`}
                >
                  In flight
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px] text-xs">
                {inFlight.tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </Link>
  );
}

// The dashboard's top-of-page KPI grid. Extracted as a presentational
// component (props in, JSX out) so the color-state and conditional-visibility
// rules can be tested without mounting the entire dashboard page. NOTE: there
// is deliberately NO "Unread Notifications" tile here — that metric was removed
// from the grid and must stay out (see the lock-health notification note below).
export function BookingRequestsMetricCard() {
  const permissions = usePermissions();
  const tierLocks = useQuery<{ deniedKeys: string[] }>({
    queryKey: ["/api/app/menu-tier-locks/denied"],
    staleTime: 60_000,
  });
  const featureGates = useFeatureGates();
  const allowed = !permissions.isLoading && permissions.canView("members") &&
    tierLocks.isSuccess && !tierLocks.data.deniedKeys.includes("members.booking_requests") &&
    featureGates.loaded && !navHiddenFor(featureGates.byKey, "/app/members/booking-requests");
  const countQuery = useQuery<{ count: number }>({
    queryKey: ["/api/app/booking-requests/submissions/unread-count"],
    enabled: allowed,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  });
  if (!allowed) return null;
  return (
    <MetricCard
      label="Booking Requests"
      value={countQuery.isError ? "Unavailable" : countQuery.data?.count ?? "—"}
      icon={CalendarClock}
      href="/app/members/booking-requests"
    />
  );
}

export function ThermostatIssuesMetricCard() {
  const roster = useQuery<Array<{ deviceId: string; issueReasons: string[] }>>({
    queryKey: ["/api/app/devices/seam/thermostats/all"],
    refetchInterval: 60_000,
  });
  const count = new Set((roster.data ?? []).filter((d) => d.issueReasons.length > 0).map((d) => d.deviceId)).size;
  return <MetricCard label="Thermostat Issues" icon={Thermometer}
    href="/app/devices/thermostats?subtab=devices&attention=1"
    value={roster.isError ? "Unavailable" : roster.isLoading ? "—" : count} />;
}

export function DashboardMetricsGrid({
  activePropertiesCount,
  totalRooms,
  occupancyPct,
  occupancyState: occupancyMetricState,
  excludeInactive,
  totalInactiveRooms,
  hasOverdueTask: showOverdueCard,
  openTasksCount,
  lastMonthProfit,
  profitMonthLabel,
  totalActiveMembers,
  isCardVisible = () => true,
}: {
  activePropertiesCount: number;
  totalRooms: number;
  occupancyPct: number;
  occupancyState: MetricState;
  excludeInactive: boolean;
  totalInactiveRooms: number;
  hasOverdueTask: boolean;
  openTasksCount: number;
  lastMonthProfit: number;
  profitMonthLabel?: string;
  totalActiveMembers: number;
  isCardVisible?: (href: string) => boolean;
}) {
  const occupancyStyles = METRIC_STATE_STYLES[occupancyMetricState];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-none xl:grid-flow-col xl:auto-cols-fr gap-3" data-tour="dashboard-metrics">
      {isCardVisible("/app/properties") && <MetricCard
        label="Active Properties"
        value={activePropertiesCount}
        icon={Building2}
        href="/app/properties"
      />}
      {isCardVisible("/app/pricing") && <MetricCard
        label="Total Rooms"
        value={totalRooms}
        icon={DoorOpen}
        href="/app/pricing"
      />}
      {isCardVisible("/app/members") && <MetricCard
        label="Total Members"
        value={totalActiveMembers}
        icon={Users}
        href="/app/members"
      />}
      {isCardVisible("/app/pricing") && <Link href="/app/pricing">
        <div
          className={`h-full border rounded-lg p-4 cursor-pointer hover:shadow-sm transition-shadow flex flex-col gap-1 ${occupancyStyles.card}`}
          data-testid="metric-card-occupancy"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Occupancy</span>
            <DoorOpen className={`h-4 w-4 ${occupancyStyles.icon}`} />
          </div>
          <span className={`text-2xl font-bold ${occupancyStyles.value}`}>{occupancyPct}%</span>
          {excludeInactive && totalInactiveRooms > 0 && (
            <span className="text-xs text-blue-600" data-testid="text-occupancy-excluding-inactive">
              Excl. {totalInactiveRooms} inactive
            </span>
          )}
        </div>
      </Link>}
      {isCardVisible("/app/financials") && <MetricCard
        label="Last Month's Profit"
        value={fmtCurrency(lastMonthProfit)}
        state={profitState(lastMonthProfit)}
        icon={DollarSign}
        href="/app/financials"
        caption={profitMonthLabel}
      />}
      {showOverdueCard && isCardVisible("/app/tasks") && (
        <MetricCard
          label="Open Tasks"
          value={openTasksCount}
          state="red"
          icon={Wrench}
          href="/app/tasks"
        />
      )}
    </div>
  );
}

type ConnectionItem = {
  source: "partner" | "inquiry";
  id: number;
  title: string;
  counterpartyName: string;
  typeLabel: string;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  unreadCount: number;
};

function ConnectionsPanel() {
  const connQuery = useQuery<ConnectionItem[]>({ queryKey: ["/api/app/connections"] });
  const items = connQuery.data || [];
  const totalUnread = items.reduce((sum, i) => sum + (i.unreadCount || 0), 0);
  const recent = items.slice(0, 5);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-connections" data-tour="dashboard-panels">
      <PanelHeader title="Connections" href="/app/connections" icon={Handshake} count={totalUnread} />
      {connQuery.isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : recent.length === 0 ? (
        <EmptyState message="No conversations yet" />
      ) : (
        <div className="space-y-2">
          {recent.map((i) => (
            <Link
              key={`${i.source}-${i.id}`}
              href={`/app/connections?source=${i.source}&id=${i.id}`}
              className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 cursor-pointer"
              data-testid={`connection-dash-row-${i.source}-${i.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{i.title}</p>
                <p className="text-xs text-gray-500 truncate">{i.counterpartyName} · {i.typeLabel}</p>
                <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{i.lastMessagePreview || "No messages yet"}</p>
              </div>
              {i.unreadCount > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold leading-none px-1.5 py-0.5 min-w-[18px] shrink-0" data-testid={`connection-dash-unread-${i.source}-${i.id}`}>
                  {i.unreadCount}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ThermostatHealthPanel({ connections, metric = false, connectionState = "ready" }: {
  connections: SeamConnection[];
  metric?: boolean;
  connectionState?: "loading" | "error" | "ready";
}) {
  const thermostatConns = connections.filter((c) => !c.deviceType || c.deviceType === "thermostat");
  const [, navigate] = useLocation();
  // Task #1979: clicking an unhealthy thermostat row or the out-of-range
  // banner deep-links to the property's Devices tab (where the trend chart
  // lives) when the device has a current assignment. Resolved at click
  // time via the shared helper so legacy alerts created while the device
  // was unassigned still upgrade once the host links it to a property.
  const handleDeviceClick = async (deviceId: string) => {
    const target = await resolveThermostatRoute(deviceId);
    navigate(target);
  };

  // Task #977: pull the user's open notifications so each unhealthy
  // thermostat row can carry the same "Stuck for X" pill the dropdown
  // shows. The query is shared with the rest of the app so the React Query
  // cache typically already has it warm.
  const notificationsQuery = useQuery<AppNotification[]>({
    queryKey: ["/api/app/notifications"],
  });
  // Re-render every minute so the duration pill ticks forward without
  // needing the user to refocus the tab or refetch the notifications.
  const [, setStuckTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStuckTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const thermostatDevicesQuery = useQuery<SeamDevice[]>({
    refetchInterval: metric ? 60_000 : false,
    queryKey: ["/api/app/devices/seam/all-thermostats", thermostatConns.map((c) => c.id).join(",")],
    queryFn: async () => {
      if (thermostatConns.length === 0) return [];
      const allDevices: SeamDevice[] = [];
      const errors: string[] = [];
      for (const conn of thermostatConns) {
        const res = await fetch(`/api/app/devices/seam/thermostats?connectionId=${conn.id}`, { credentials: "include" });
        if (res.ok) {
          const devices = await res.json();
          allDevices.push(...devices);
        } else {
          errors.push(`Connection ${conn.id} failed (${res.status})`);
        }
      }
      if (errors.length > 0) {
        throw new Error(errors.join("; "));
      }
      // Seam's /thermostats/list returns every device visible to the API key,
      // so when a host has multiple Seam connections each per-connection call
      // returns the same union of devices. Dedupe by device_id so the offline /
      // out-of-range counts (and the rendered row list) don't multiply by the
      // number of connections.
      const seen = new Set<string>();
      const deduped: SeamDevice[] = [];
      for (const d of allDevices) {
        if (!d?.device_id || seen.has(d.device_id)) continue;
        seen.add(d.device_id);
        deduped.push(d);
      }
      return deduped;
    },
    enabled: thermostatConns.length > 0,
  });

  // Lookups so the panel can apply each property's per-property out-of-range
  // threshold (defaults to 2°F when a device hasn't been linked to a property
  // or the property has no override). Both endpoints are already used
  // elsewhere in the app, so they're typically already cached.
  const assignmentsQuery = useQuery<Array<{ deviceId: string; propertyId: number }>>({
    queryKey: ["/api/app/devices/thermostats/property-assignments"],
    enabled: thermostatConns.length > 0,
  });
  const propertiesForThresholdQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
    enabled: thermostatConns.length > 0,
  });

  const thresholdByDevice = useMemo(() => {
    const propThreshold = new Map<number, number>();
    for (const p of propertiesForThresholdQuery.data || []) {
      propThreshold.set(p.id, p.thermostatHealthThresholdF ?? DEFAULT_THERMOSTAT_HEALTH_THRESHOLD_F);
    }
    const map = new Map<string, number>();
    for (const a of assignmentsQuery.data || []) {
      const t = propThreshold.get(a.propertyId);
      if (t != null) map.set(a.deviceId, t);
    }
    return map;
  }, [assignmentsQuery.data, propertiesForThresholdQuery.data]);

  const isLoading = thermostatDevicesQuery.isLoading;
  const allDevices = thermostatDevicesQuery.data || [];
  const offlineCount = allDevices.filter((d) => !d.properties?.online).length;
  const outOfRangeDevices = useMemo(() => {
    return allDevices
      .map((d) => ({
        device: d,
        issue: evaluateOutOfRange(d, thresholdByDevice.get(d.device_id) ?? DEFAULT_THERMOSTAT_HEALTH_THRESHOLD_F),
      }))
      .filter((row): row is { device: SeamDevice; issue: NonNullable<ReturnType<typeof evaluateOutOfRange>> } => row.issue !== null);
  }, [allDevices, thresholdByDevice]);
  const outOfRangeCount = outOfRangeDevices.length;
  const issueByDevice = useMemo(
    () => new Map(outOfRangeDevices.map((r) => [r.device.device_id, r.issue])),
    [outOfRangeDevices],
  );
  // Summarize the thresholds among the flagged thermostats so the banner can
  // be specific (e.g. "more than 5°F off setpoint") even when properties have
  // their own per-property overrides ("more than 1–5°F off setpoint").
  const outOfRangeThresholdSummary = useMemo(() => {
    if (outOfRangeDevices.length === 0) return null;
    const thresholds = outOfRangeDevices.map((r) => r.issue.thresholdF);
    const min = Math.min(...thresholds);
    const max = Math.max(...thresholds);
    return min === max ? `${min}°F` : `${min}–${max}°F`;
  }, [outOfRangeDevices]);

  // Unhealthy = offline OR out-of-range. Only these are shown in the card rows.
  const unhealthyDevices = useMemo(() => {
    return allDevices.filter((d) => !d.properties?.online || issueByDevice.has(d.device_id));
  }, [allDevices, issueByDevice]);

  if (metric) {
    const failed = connectionState === "error" || thermostatDevicesQuery.isError ||
      assignmentsQuery.isError || propertiesForThresholdQuery.isError;
    const loading = connectionState === "loading" || isLoading ||
      (thermostatConns.length > 0 && (assignmentsQuery.isLoading || propertiesForThresholdQuery.isLoading));
    return <MetricCard label="Thermostat Issues" icon={Thermometer} href="/app/devices/thermostats"
      value={failed ? "Unavailable" : loading ? "—" : unhealthyDevices.length} />;
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-thermostat-health">
      <PanelHeader title="Thermostat Health" href="/app/devices?tab=thermostats" icon={Thermometer} />
      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : thermostatDevicesQuery.isError ? (
        <div className="py-4 text-center text-sm text-red-500" data-testid="text-thermostat-error">Failed to load thermostat data</div>
      ) : allDevices.length === 0 ? (
        <EmptyState message="No thermostats connected" />
      ) : unhealthyDevices.length === 0 ? (
        <div className="py-4 text-center text-sm text-green-600 flex flex-col items-center gap-1" data-testid="text-thermostat-all-healthy">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          All thermostats healthy
        </div>
      ) : (
        <>
          {offlineCount > 0 && (
            <div className="mb-2 flex items-center gap-1.5 text-xs text-red-600 font-medium bg-red-50 border border-red-100 rounded px-2 py-1.5" data-testid="banner-offline-thermostats">
              <AlertTriangle className="h-3.5 w-3.5" />
              {offlineCount} device{offlineCount > 1 ? "s" : ""} offline
            </div>
          )}
          {outOfRangeCount > 0 && (
            // Task #1979: the banner is a CTA into the property Devices tab
            // for the first out-of-range device. With a single flagged device
            // this routes straight to the property + thermostat card (where
            // the trend chart lives); with several, the host lands on the
            // first one's property and can navigate from there.
            <button
              type="button"
              className="w-full mb-2 flex items-center gap-1.5 text-xs text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded px-2 py-1.5 hover:bg-amber-100 transition-colors text-left"
              onClick={() => handleDeviceClick(outOfRangeDevices[0].device.device_id)}
              data-testid="banner-out-of-range-thermostats"
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {outOfRangeCount} thermostat{outOfRangeCount > 1 ? "s" : ""} more than {outOfRangeThresholdSummary} off setpoint
            </button>
          )}
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {unhealthyDevices.slice(0, 6).map((device) => {
              const tempF = device.properties?.temperature_fahrenheit ?? device.properties?.temperature;
              const issue = issueByDevice.get(device.device_id) ?? null;
              const isOffline = !device.properties?.online;
              const rowClass = isOffline
                ? "border-red-200 bg-red-50"
                : issue
                  ? "border-amber-200 bg-amber-50"
                  : "border-gray-100 bg-gray-50";
              // Task #977: only surface "Stuck for X" when there is actually
              // an open thermostat_health alert for this device — that way
              // the pill disappears the moment the user acks the alert and
              // never lingers on a thermostat that's already healthy.
              const stuckSince = findOpenThermostatStuckSince(
                notificationsQuery.data ?? [],
                device.device_id,
              );
              const stuckLabel = stuckSince ? formatStuckDuration(stuckSince) : null;
              return (
                // Task #1979: each unhealthy thermostat row deep-links to the
                // property's Devices tab for that device (where the trend
                // chart lives) when a current assignment exists; falls back
                // to the global Thermostats page when truly unassigned.
                <button
                  type="button"
                  key={device.device_id}
                  onClick={() => handleDeviceClick(device.device_id)}
                  className={`w-full flex items-start justify-between p-2 rounded border text-sm text-left hover:brightness-95 transition-[filter] ${rowClass}`}
                  data-testid={`thermostat-row-${device.device_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {device.properties?.online ? (
                        <Wifi className="h-3.5 w-3.5 text-green-500 shrink-0" />
                      ) : (
                        <WifiOff className="h-3.5 w-3.5 text-red-500 shrink-0" />
                      )}
                      <span className="truncate font-medium text-gray-900">{device.display_name}</span>
                      {stuckLabel && (
                        <span
                          className="shrink-0 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200"
                          title={`First detected ${format(stuckSince!, "MMM d, h:mm a")}`}
                          aria-label={`Stuck for ${stuckLabel}`}
                          data-testid={`thermostat-row-stuck-${device.device_id}`}
                        >
                          Stuck for {stuckLabel}
                        </span>
                      )}
                    </div>
                    {device.location?.location_name && (
                      <p className="text-xs text-gray-500 mt-0.5 ml-5 truncate">{device.location.location_name}</p>
                    )}
                    {issue ? (
                      <p
                        className="text-xs mt-0.5 ml-5 font-medium text-amber-700"
                        data-testid={`text-thermostat-out-of-range-${device.device_id}`}
                      >
                        {issue.kind === "above_cool"
                          ? `${Math.round(issue.tempF)}°F · ${issue.deltaF.toFixed(1)}°F above cool ${Math.round(issue.setF)}°F (>${issue.thresholdF}°F threshold)`
                          : `${Math.round(issue.tempF)}°F · ${issue.deltaF.toFixed(1)}°F below heat ${Math.round(issue.setF)}°F (>${issue.thresholdF}°F threshold)`}
                      </p>
                    ) : (
                      <p className={`text-xs mt-0.5 ml-5 font-medium ${device.properties?.online ? "text-green-600" : "text-red-600"}`}>
                        {device.properties?.online ? "Online" : "Offline"}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0 ml-2">
                    {tempF !== undefined && (
                      <span className="text-xs text-gray-600">{Math.round(tempF)}°F</span>
                    )}
                    {device.properties?.current_climate_setting?.hvac_mode_setting && (
                      <span className="text-xs text-gray-400 capitalize">{device.properties.current_climate_setting.hvac_mode_setting}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function MaintenancePanel({ tasks, properties }: { tasks: MaintenanceTask[]; properties: Property[] }) {
  const urgentOrHigh = useMemo(() => {
    const now = new Date();
    return tasks
      .filter((t) => {
        if (t.status === "complete") return false;
        const isUrgentOrHigh = t.priority === "urgent" || t.priority === "high";
        const isOverdue = t.dueDate != null && new Date(t.dueDate) < now;
        return isUrgentOrHigh || isOverdue;
      })
      .sort((a, b) => {
        const priorityOrder: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };
        const aPriority = priorityOrder[a.priority] ?? 4;
        const bPriority = priorityOrder[b.priority] ?? 4;
        if (aPriority !== bPriority) return aPriority - bPriority;
        if (a.dueDate && b.dueDate) return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        return 0;
      })
      .slice(0, 5);
  }, [tasks]);

  const PRIORITY_COLORS: Record<string, string> = {
    urgent: "bg-red-100 text-red-800",
    high: "bg-orange-100 text-orange-800",
    medium: "bg-yellow-100 text-yellow-800",
    low: "bg-green-100 text-green-800",
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-maintenance">
      <PanelHeader title="Open Tasks" href="/app/tasks" icon={Wrench} />
      {urgentOrHigh.length === 0 ? (
        <EmptyState message="No urgent, high priority, or overdue tasks" />
      ) : (
        <div className="space-y-2">
          {urgentOrHigh.map((task) => {
            const property = task.propertyId ? properties.find((p) => p.id === task.propertyId) : null;
            const now = new Date();
            const isOverdue = task.dueDate != null && new Date(task.dueDate) < now;
            return (
              <Link href={`/app/tasks?task=${task.id}`} key={task.id}>
                <div
                  className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${isOverdue ? "border-red-200 bg-red-50" : "border-gray-100 bg-gray-50"}`}
                  data-testid={`maintenance-row-${task.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <Badge className={`text-xs py-0 h-4 ${PRIORITY_COLORS[task.priority]}`}>
                        {task.priority}
                      </Badge>
                      {isOverdue && (
                        <Badge className="text-xs py-0 h-4 bg-red-600 text-white">overdue</Badge>
                      )}
                      {property && <span className="text-xs text-gray-500 truncate">{property.name}</span>}
                    </div>
                    <p className="text-sm text-gray-800 line-clamp-1">{task.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-400 capitalize">{task.status.replace(/_/g, " ")}</span>
                      {task.dueDate && (
                        <span className={`text-xs ${isOverdue ? "text-red-600 font-medium" : "text-gray-400"}`}>
                          · Due {format(new Date(task.dueDate), "MMM d")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Task #1029 — The legacy `message_drafts` table was dropped. Drafts are now
// modeled as messages_pending rows with auto_send=false, surfaced on the
// dedicated /app/messages/pending page. This panel now redirects there.
import type { MessagesPending } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";

function DraftMessagesMetricCard() {
  const pendingQuery = useQuery<MessagesPending[]>({ queryKey: ["/api/app/messages/pending"], refetchInterval: 10_000 });
  const awaiting = (pendingQuery.data ?? []).filter(isDraftMessage);

  return (
    <MetricCard label="Draft Messages" icon={FileText} href="/app/messages?tab=drafts"
      value={pendingQuery.isError ? "Unavailable" : pendingQuery.isLoading ? "—" : awaiting.length} />
  );
}

function AwaitingRepliesMetricCard({ activePropertyIds }: { activePropertyIds: Set<number> | null }) {
  const query = useQuery<InboxRow[]>({ queryKey: ["/api/app/inbox/waiting-reply"], refetchInterval: 10_000 });
  const rows = query.data ?? [];
  const count = activePropertyIds
    ? rows.filter(row => row.propertyId != null && activePropertyIds.has(row.propertyId)).length
    : rows.length;
  return <MetricCard label="Awaiting Replies" icon={Hourglass} href="/app/messages?tab=waitingReplies"
    value={query.isError ? "Unavailable" : query.isLoading ? "—" : count} />;
}

function SupplyRequestsMetricCard() {
  const query = useQuery<SupplyOrder[]>({ queryKey: ["/api/app/supply-orders"], refetchInterval: 10_000 });
  return <MetricCard label="Supply Requests" icon={ShoppingCart} href="/app/supplies"
    value={query.isError ? "Unavailable" : query.isLoading ? "—" : (query.data ?? []).filter(order => order.status === "requested").length} />;
}

function PendingApprovalsPanel({ vendors, properties, activePropertyIds }: { vendors: Vendor[]; properties: Property[]; activePropertyIds: Set<number> | null }) {
  const approvalsQuery = useQuery<OperationsApproval[]>({ queryKey: ["/api/app/operations/approvals"] });
  const allApprovals = approvalsQuery.data || [];
  const approvals = useMemo(() => {
    if (!activePropertyIds) return allApprovals;
    return allApprovals.filter(a => a.propertyId != null && activePropertyIds.has(a.propertyId));
  }, [allApprovals, activePropertyIds]);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-approvals">
      <PanelHeader title="Pending Vendor Payments" href="/app/operations/approvals" icon={CreditCard} />
      {approvalsQuery.isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : approvals.length === 0 ? (
        <EmptyState message="No pending approvals" />
      ) : (
        <div className="space-y-2">
          {approvals.slice(0, 5).map((approval) => {
            const vendor = approval.vendorId ? vendors.find((v) => v.id === approval.vendorId) : null;
            const property = approval.propertyId ? properties.find((p) => p.id === approval.propertyId) : null;
            return (
              <div
                key={approval.id}
                className="flex items-start justify-between p-2.5 rounded-lg border border-gray-100 bg-gray-50"
                data-testid={`approval-row-${approval.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Badge variant="outline" className="text-xs h-4 py-0 capitalize">{approval.type.replace(/_/g, " ")}</Badge>
                  </div>
                  <p className="text-sm font-medium text-gray-900 line-clamp-1">{approval.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {vendor && <span className="text-xs text-gray-500">{vendor.name}</span>}
                    {property && <span className="text-xs text-gray-400">{property.name}</span>}
                  </div>
                </div>
                {approval.priceCents != null && approval.priceCents > 0 && (
                  <span className="text-sm font-semibold text-gray-900 shrink-0 ml-2">{fmtCurrency(approval.priceCents / 100)}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Task #1029 — Inbox-driven dashboard panels. The unread count comes from
// /api/app/inbox so we no longer need conversations or per-message rows.
type InboxRow = {
  memberId: number;
  memberName: string;
  propertyId: number | null;
  propertyName: string | null;
  lastChatId: number | null;
  lastChatAt: string | null;
  lastChatPreview: string | null;
  pendingCount: number;
  // Task #1866 — present on rows from /api/app/inbox/waiting-reply only.
  waitingReplyAt?: string | null;
};

function InboxMediaIcon({
  type,
  className,
}: {
  type: "image" | "video" | "audio" | "media";
  className?: string;
}) {
  const Icon =
    type === "video" ? Video : type === "audio" ? Mic : type === "media" ? Paperclip : ImageIcon;
  return <Icon className={className} aria-hidden="true" />;
}

function groupPropertiesParentChild(properties: Property[]): Property[] {
  const parents = properties.filter((p) => !p.parentPropertyId).sort((a, b) => a.name.localeCompare(b.name));
  const childrenByParent = new Map<number, Property[]>();
  for (const p of properties) {
    if (p.parentPropertyId) {
      const list = childrenByParent.get(p.parentPropertyId) || [];
      list.push(p);
      childrenByParent.set(p.parentPropertyId, list);
    }
  }
  for (const [, children] of childrenByParent) {
    children.sort((a, b) => a.name.localeCompare(b.name));
  }
  const result: Property[] = [];
  for (const parent of parents) {
    result.push(parent);
    const children = childrenByParent.get(parent.id) || [];
    result.push(...children);
  }
  const orphans = properties.filter((p) => p.parentPropertyId && !parents.some((par) => par.id === p.parentPropertyId));
  result.push(...orphans);
  return result;
}

function OccupancyPanel({ properties, rooms, excludeInactive }: { properties: Property[]; rooms: Array<SchemaRoom & { propertyName: string; propertyAddress: string | null; propertyStatus: string }>; excludeInactive: boolean }) {
  const activeProperties = properties.filter((p) => p.status === "active");

  const occupancyData = useMemo(() => {
    const grouped = groupPropertiesParentChild(activeProperties);
    return grouped.map((p) => {
      const propertyRooms = rooms.filter((r) => r.propertyId === p.id && r.roomType !== "bathroom" && !r.shadowOfRoomId);
      const stats = computeOccupancy(propertyRooms, excludeInactive);
      return { property: p, ...stats };
    });
  }, [activeProperties, rooms, excludeInactive]);

  const totalInactive = occupancyData.reduce((s, d) => s + d.inactiveRooms, 0);
  const totalVacant = occupancyData.reduce((s, d) => s + d.vacantRooms, 0);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-occupancy">
      <PanelHeader title="Occupancy Overview" href="/app/properties" icon={Building2} />
      {excludeInactive && totalInactive > 0 && (
        <div className="mb-2 text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded px-2 py-1.5 font-medium" data-testid="banner-excluding-inactive-rooms">
          Excluding {totalInactive} inactive room{totalInactive > 1 ? "s" : ""}
        </div>
      )}
      {occupancyData.length === 0 ? (
        <EmptyState message="No active properties" />
      ) : (
        <>
          {totalVacant > 0 && (
            <div className="mb-2 text-xs text-orange-600 bg-orange-50 border border-orange-100 rounded px-2 py-1.5 font-medium" data-testid="banner-vacant-rooms">
              {totalVacant} vacant room{totalVacant > 1 ? "s" : ""} across your portfolio
            </div>
          )}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {occupancyData.map(({ property, totalConsideredRooms, occupiedRooms, vacantRooms, inactiveRooms }) => (
              <div
                key={property.id}
                className={`flex items-center justify-between p-2 rounded border border-gray-100 bg-gray-50 text-sm ${property.parentPropertyId ? "ml-4" : ""}`}
                data-testid={`occupancy-row-${property.id}`}
              >
                <span className="font-medium text-gray-900 truncate flex-1 mr-2">
                  {property.parentPropertyId ? `\u2514 ${property.name}` : property.name}
                </span>
                <div className="flex items-center gap-2 shrink-0 text-xs text-gray-500">
                  <span className="text-green-600 font-medium">{occupiedRooms} occupied</span>
                  {vacantRooms > 0 && <span className="text-orange-600 font-medium">{vacantRooms} vacant</span>}
                  {excludeInactive && inactiveRooms > 0 && (
                    <span className="text-gray-400">excl. {inactiveRooms} inactive</span>
                  )}
                  <span className="text-gray-400">/ {totalConsideredRooms}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RenovationsStatusPanel({ properties }: { properties: Property[] }) {
  const renoProperties = properties.filter((p) => p.status === "renovations");
  const [itemsByProperty, setItemsByProperty] = useState<Record<number, RenovationItem[]>>({});
  const [expensesByProperty, setExpensesByProperty] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (renoProperties.length === 0) { setLoading(false); return; }
    let cancelled = false;
    async function loadData() {
      const itemsMap: Record<number, RenovationItem[]> = {};
      const expMap: Record<number, number> = {};

      const [expRes, ...itemResults] = await Promise.all([
        fetch("/api/app/expenses", { credentials: "include" }).then(r => r.ok ? r.json() : []).catch(() => []),
        ...renoProperties.map(p =>
          fetch(`/api/app/properties/${p.id}/renovation-items`, { credentials: "include" }).then(r => r.ok ? r.json() : []).catch(() => [])
        ),
      ]);

      const allExpenses: { amount: string; renovationItemId?: number }[] = expRes;
      const itemIdToPropertyId: Record<number, number> = {};

      renoProperties.forEach((p, i) => {
        const items: RenovationItem[] = itemResults[i] || [];
        itemsMap[p.id] = items;
        items.forEach(item => { itemIdToPropertyId[item.id] = p.id; });
      });

      allExpenses.forEach(exp => {
        if (exp.renovationItemId) {
          const propId = itemIdToPropertyId[exp.renovationItemId];
          if (propId) expMap[propId] = (expMap[propId] || 0) + (parseFloat(exp.amount) || 0);
        }
      });

      if (!cancelled) {
        setItemsByProperty(itemsMap);
        setExpensesByProperty(expMap);
        setLoading(false);
      }
    }
    loadData();
    return () => { cancelled = true; };
  }, [renoProperties.map(p => p.id).join(","), renoProperties.map(p => p.renovationStatus).join(",")]);

  function getCompletionDate(p: Property, items: RenovationItem[]): string | null {
    if (!p.renovationStartDate) return null;
    const totalDays = items.reduce((s, i) => s + (i.estimatedDays || 0), 0) + (p.permitsRequired ? (p.permitDays || 0) : 0);
    const start = new Date(p.renovationStartDate + "T00:00:00");
    start.setDate(start.getDate() + totalDays);
    return format(start, "MMM d, yyyy");
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-renovations-status">
      <PanelHeader title="Renovations Status" href="/app/renovations" icon={Hammer} />
      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : renoProperties.length === 0 ? (
        <EmptyState message="No active renovations" />
      ) : (
        <div className="space-y-2">
          {renoProperties.slice(0, 5).map((p) => {
            const items = itemsByProperty[p.id] || [];
            const totalBudget = items.reduce((s, i) => s + (i.budget ? parseFloat(i.budget) || 0 : 0), 0);
            const totalSpent = expensesByProperty[p.id] || 0;
            const budgetPct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;
            const completionDate = getCompletionDate(p, items);

            return (
              <Link key={p.id} href={`/app/renovations/${p.id}`}>
                <div
                  className="p-2.5 rounded border border-gray-100 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                  data-testid={`reno-row-${p.id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-800 truncate" data-testid={`reno-name-${p.id}`}>{p.name}</span>
                    {p.renovationStatus && (
                      <Badge variant="outline" className="text-xs ml-2 shrink-0">
                        {p.renovationStatus === "in_progress" ? "In Progress" : p.renovationStatus === "complete" ? "Complete" : "Not Started"}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    {completionDate ? (
                      <span data-testid={`reno-completion-${p.id}`}>Est. completion: {completionDate}</span>
                    ) : (
                      <span className="text-gray-400">No schedule set</span>
                    )}
                    {totalBudget > 0 ? (
                      <span className={budgetPct > 100 ? "text-red-600 font-medium" : "text-gray-600"} data-testid={`reno-budget-pct-${p.id}`}>
                        {budgetPct}% of budget spent
                      </span>
                    ) : (
                      <span className="text-gray-400">No budget set</span>
                    )}
                  </div>
                  {totalBudget > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-1 mt-1.5">
                      <div
                        className={`h-1 rounded-full transition-all ${budgetPct > 100 ? "bg-red-500" : budgetPct > 75 ? "bg-amber-500" : "bg-blue-500"}`}
                        style={{ width: `${Math.min(budgetPct, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FinancialSnapshotPanel({ activePropertyIds }: { activePropertyIds: Set<number> | null }) {
  const expensesQuery = useQuery<{ category: string; amount: string; date: string; propertyId?: number | null }[]>({ queryKey: ["/api/app/expenses"] });
  const financialsQuery = useQuery<PadsplitFinancial[]>({ queryKey: ["/api/app/padsplit/financials"] });

  const snapshot = useMemo(() => {
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

    const allExpenses = expensesQuery.data || [];
    const expenses = !activePropertyIds
      ? allExpenses
      : allExpenses.filter(e => e.propertyId != null && activePropertyIds.has(e.propertyId));
    const totalExpenses = expenses
      .filter((e) => e.date && e.date.startsWith(currentMonthKey))
      .reduce((sum, e) => sum + parseFloat(e.amount || "0"), 0);

    const financials = financialsQuery.data || [];
    const groupFilteredFinancials = !activePropertyIds
      ? financials
      : financials.filter(fin => fin.propertyId != null && activePropertyIds.has(fin.propertyId as number));
    const currentMonthFinancials = groupFilteredFinancials.filter((fin) => {
      if (fin.periodStart) return fin.periodStart.startsWith(currentMonthKey);
      if (fin.periodEnd) return fin.periodEnd.startsWith(currentMonthKey);
      return false;
    });
    const totalEarnings = currentMonthFinancials.reduce((sum, fin) => {
      return sum + parseFloat(fin.totalEarnings || "0");
    }, 0);
    const managementFees = currentMonthFinancials.reduce((sum, fin) => {
      return sum + parseFloat(fin.managementFees || "0");
    }, 0);

    return {
      totalCollections: totalEarnings,
      managementFees,
      totalExpenses,
      net: totalEarnings - managementFees - totalExpenses,
      monthLabel: now.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    };
  }, [expensesQuery.data, financialsQuery.data, activePropertyIds]);

  const isLoading = expensesQuery.isLoading || financialsQuery.isLoading;

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-financial-snapshot">
      <PanelHeader title="Financial Snapshot" href="/app/financials" icon={DollarSign} />
      <p className="text-xs text-gray-500 mb-3">{snapshot.monthLabel}</p>
      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2.5 rounded bg-green-50 border border-green-100" data-testid="text-total-collections">
            <span className="text-sm text-gray-600">Total Earnings</span>
            <span className="text-sm font-semibold text-green-700">{fmtCurrency(snapshot.totalCollections)}</span>
          </div>
          {snapshot.managementFees > 0 && (
            <div className="flex items-center justify-between p-2.5 rounded bg-orange-50 border border-orange-100" data-testid="text-management-fees">
              <span className="text-sm text-gray-600">Management Fees</span>
              <span className="text-sm font-semibold text-orange-700">-{fmtCurrency(snapshot.managementFees)}</span>
            </div>
          )}
          {snapshot.totalExpenses > 0 && (
            <div className="flex items-center justify-between p-2.5 rounded bg-red-50 border border-red-100" data-testid="text-total-expenses">
              <span className="text-sm text-gray-600">Expenses</span>
              <span className="text-sm font-semibold text-red-700">-{fmtCurrency(snapshot.totalExpenses)}</span>
            </div>
          )}
          <div className={`flex items-center justify-between p-2.5 rounded border ${snapshot.net >= 0 ? "bg-blue-50 border-blue-100" : "bg-orange-50 border-orange-100"}`} data-testid="text-net-income">
            <span className="text-sm font-medium text-gray-700">Net</span>
            <span className={`text-sm font-bold ${snapshot.net >= 0 ? "text-blue-700" : "text-orange-700"}`}>{fmtCurrency(snapshot.net)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function LockHealthPanel({ connections, metric = false, connectionState = "ready" }: {
  connections: SeamConnection[];
  metric?: boolean;
  connectionState?: "loading" | "error" | "ready";
}) {
  // Fix: canonical deviceType is "smart_lock", not "lock"
  const lockConns = connections.filter((c) => c.deviceType === "smart_lock");

  const lockDevicesQuery = useQuery<SeamDevice[]>({
    queryKey: ["/api/app/devices/seam/all-locks", lockConns.map((c) => c.id).join(",")],
    refetchInterval: metric ? 60_000 : false,
    queryFn: async () => {
      if (lockConns.length === 0) return [];
      const allDevices: SeamDevice[] = [];
      for (const conn of lockConns) {
        const res = await fetch(`/api/app/devices/seam/locks?connectionId=${conn.id}`, { credentials: "include" });
        if (!res.ok) throw new Error(`Failed to load locks for connection ${conn.id}`);
        if (res.ok) {
          const data = await res.json();
          const devices = Array.isArray(data) ? data : data.locks || [];
          allDevices.push(...devices);
        }
      }
      return allDevices;
    },
    enabled: lockConns.length > 0,
  });

  // TTLock connections — needed to detect TTLock presence and surface alerts
  const ttlockConnectionsQuery = useQuery<Array<{ id: number; ttlockUsername: string; status: string }>>({
    queryKey: ["/api/app/devices/ttlock/connections"],
    refetchInterval: metric ? 60_000 : false,
  });
  const ttlockConnections = ttlockConnectionsQuery.data || [];
  const connectedTtlockConnections = useMemo(
    () => ttlockConnections.filter((c) => c.status === "connected"),
    [ttlockConnections],
  );
  const hasTtlock = connectedTtlockConnections.length > 0;

  // TTLock cached locks across every connected account. Source-of-truth
  // for: total TTLock device count (needed for "All N locks healthy"
  // copy) and offline parity (hasGateway === false ⇒ offline, matching
  // the smart-locks page and bell semantics).
  type TtlockLite = {
    id: number;
    ttlockLockId: number;
    lockAlias: string | null;
    lockName: string | null;
    hasGateway: boolean;
    connectionId: number;
    propertyId?: number | null;
  };
  const ttlockLocksQuery = useQuery<TtlockLite[]>({
    refetchInterval: metric ? 60_000 : false,
    queryKey: [
      "/api/app/dashboard/ttlock-locks-aggregate",
      connectedTtlockConnections.map((c) => c.id).join(","),
    ],
    queryFn: async () => {
      if (connectedTtlockConnections.length === 0) return [];
      // Any per-connection failure must propagate so the panel surfaces
      // the error state instead of silently understating offline /
      // total lock counts (which would otherwise let problems hide
      // behind a healthy-looking UI).
      const results = await Promise.all(
        connectedTtlockConnections.map(async (conn) => {
          const res = await fetch(
            `/api/app/devices/ttlock/connections/${conn.id}/locks`,
            { credentials: "include" },
          );
          if (!res.ok) throw new Error(`Failed to load TTLock locks for connection ${conn.id}`);
          const data = await res.json();
          return Array.isArray(data) ? (data as TtlockLite[]) : [];
        }),
      );
      return results.flat();
    },
    enabled: hasTtlock,
  });
  const ttlockLocks = ttlockLocksQuery.data || [];

  // Low-battery notifications table — the canonical source for TTLock
  // low-battery alerts (TTLock device IDs are prefixed "ttlock-"; Seam
  // entries are handled via raw devices above).
  const lowBatteryQuery = useQuery<LockBatteryNotification[]>({
    queryKey: ["/api/app/devices/low-battery-notifications"],
    refetchInterval: metric ? 60_000 : false,
    enabled: hasTtlock,
  });
  // Task #1699 — `lock_battery_notifications` now holds both low-battery
  // AND offline TTLock entries. Split by the explicit `batteryLevel`
  // flag so the Lock Health banner counts/labels stay correct: a row
  // counts as low-battery only when `batteryLevel` is non-null.
  // Offline rows are still driven by the cached-locks query
  // (`ttlockOfflineLocks` below) since that's the live source of
  // truth — the notification table is just secondary persistence so
  // the /app/devices Notifications bell can surface offline events.
  const ttlockLowBatteryNotifications = useMemo(() => {
    if (!hasTtlock) return [];
    return (lowBatteryQuery.data || []).filter(
      (n) => n.deviceId.startsWith("ttlock-") && n.batteryLevel !== null,
    );
  }, [lowBatteryQuery.data, hasTtlock]);

  // TTLock offline detection mirrors the smart-locks page: a TTLock
  // lock is "offline" when it has no working gateway (hasGateway===false
  // or null). Without a gateway TTLock cannot relay commands so the
  // lock is effectively unreachable, the same severity tier as a Seam
  // bridge-offline lock.
  // Task #2325 — only surface alerts for locks that are assigned to a
  // property (propertyId != null). Unassigned locks are not live and
  // must not inflate the offline count.
  const ttlockOfflineLocks = useMemo(
    // hasGateway !== true catches both `false` and `null` — without a
    // working gateway TTLock cannot relay commands, so either value
    // means the lock is effectively offline (matches smart-locks page).
    () => ttlockLocks.filter((l) => l.hasGateway !== true && l.propertyId != null),
    [ttlockLocks],
  );

  const isLoading =
    lockDevicesQuery.isLoading ||
    ttlockConnectionsQuery.isLoading ||
    (hasTtlock && (lowBatteryQuery.isLoading || ttlockLocksQuery.isLoading));

  const allLocks = lockDevicesQuery.data || [];

  // Task #2325 — only include Seam locks that are assigned to a property
  // (`__padpulse_unassigned` is false/absent). Locks with no property
  // assignment are not live and must not appear in health alerts.
  const seamProblemLocks = useMemo(() => {
    return allLocks.filter((d) => {
      if (d.__padpulse_unassigned) return false;
      const offline = !d.properties?.online;
      const lowBattery = d.properties?.battery?.level != null && d.properties.battery.level < 0.2;
      return offline || lowBattery;
    });
  }, [allLocks]);

  const seamOfflineCount = seamProblemLocks.filter((d) => !d.properties?.online).length;
  const offlineCount = seamOfflineCount + ttlockOfflineLocks.length;
  const seamLowBatteryCount = seamProblemLocks.filter(
    (d) => d.properties?.battery?.level != null && d.properties.battery.level < 0.2
  ).length;
  const lowBatteryCount = seamLowBatteryCount + ttlockLowBatteryNotifications.length;

  // Total lock count across both providers; drives the "All N locks
  // healthy" copy so TTLock-only hosts no longer see "All 0 locks".
  const totalLockCount = allLocks.length + ttlockLocks.length;

  // Empty state should reflect actual discovered devices, not just the
  // presence of a connection — a connected account with zero locks
  // must still show "No locks connected" instead of "All 0 healthy".
  const hasAnyLocks = totalLockCount > 0;
  const hasProblems =
    seamProblemLocks.length > 0 ||
    ttlockLowBatteryNotifications.length > 0 ||
    ttlockOfflineLocks.length > 0;

  if (metric) {
    const issueLocks = new Set([
      ...seamProblemLocks.map(device => device.device_id),
      ...ttlockOfflineLocks.map(device => `ttlock-${device.ttlockLockId}`),
      ...ttlockLowBatteryNotifications.map(notification => notification.deviceId),
    ]);
    const failed = connectionState === "error" || lockDevicesQuery.isError || ttlockConnectionsQuery.isError ||
      (hasTtlock && (lowBatteryQuery.isError || ttlockLocksQuery.isError));
    return <MetricCard label="Lock Issues" icon={Lock} href="/app/devices/smart-locks"
      value={failed ? "Unavailable" : connectionState === "loading" || isLoading ? "—" : issueLocks.size} />;
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-lock-health">
      <PanelHeader title="Lock Health" href="/app/devices" icon={Lock} />
      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : lockDevicesQuery.isError || ttlockConnectionsQuery.isError || (hasTtlock && (lowBatteryQuery.isError || ttlockLocksQuery.isError)) ? (
        <div className="py-4 text-center text-sm text-red-500" data-testid="text-lock-error">Failed to load lock data</div>
      ) : !hasAnyLocks ? (
        <EmptyState message="No locks connected" />
      ) : !hasProblems ? (
        <div className="flex items-center gap-2 p-3 rounded bg-green-50 border border-green-100" data-testid="text-locks-healthy">
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
          {/* Count spans both providers (Seam devices + cached TTLock
              locks) so TTLock-only hosts see the correct total. */}
          <span className="text-sm text-green-700">
            All {totalLockCount} lock{totalLockCount !== 1 ? "s" : ""} healthy
          </span>
        </div>
      ) : (
        <>
          <div className="mb-2 flex items-center gap-1.5 text-xs text-red-600 font-medium bg-red-50 border border-red-100 rounded px-2 py-1.5" data-testid="banner-lock-issues">
            <AlertTriangle className="h-3.5 w-3.5" />
            {offlineCount > 0 && `${offlineCount} offline`}
            {offlineCount > 0 && lowBatteryCount > 0 && " · "}
            {lowBatteryCount > 0 && `${lowBatteryCount} low battery`}
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {seamProblemLocks.slice(0, 6).map((device) => {
              const isOffline = !device.properties?.online;
              const batteryPct = device.properties?.battery?.level != null
                ? Math.round(device.properties.battery.level * 100)
                : null;
              const isLowBattery = batteryPct != null && batteryPct < 20;
              return (
                <div
                  key={device.device_id}
                  className="flex items-start justify-between p-2 rounded border border-red-200 bg-red-50 text-sm"
                  data-testid={`lock-issue-row-${device.device_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {isOffline ? (
                        <WifiOff className="h-3.5 w-3.5 text-red-500 shrink-0" />
                      ) : (
                        <BatteryWarning className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                      )}
                      <span className="truncate font-medium text-gray-900">{device.display_name}</span>
                    </div>
                    {device.location?.location_name && (
                      <p className="text-xs text-gray-500 mt-0.5 ml-5">{device.location.location_name}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0 ml-2">
                    {isOffline && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Offline</Badge>}
                    {isLowBattery && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50">
                        {batteryPct}% battery
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
            {(() => {
              // Render TTLock offline locks first, then low-battery
              // notifications, dedup'd by deviceId. Cap the combined
              // TTLock list to whatever slots remain after Seam rows so
              // the panel stays within ~6 entries.
              const remaining = Math.max(0, 6 - seamProblemLocks.length);
              const offlineRows = ttlockOfflineLocks.map((l) => ({
                kind: "offline" as const,
                deviceId: `ttlock-${l.connectionId}-${l.ttlockLockId}`,
                name: l.lockAlias || l.lockName || `Lock ${l.ttlockLockId}`,
                batteryLevel: null as number | null,
                locationName: null as string | null,
              }));
              const lowBatteryRows = ttlockLowBatteryNotifications
                .filter((n) => !offlineRows.some((o) => o.deviceId === n.deviceId))
                .map((n) => ({
                  kind: "low_battery" as const,
                  deviceId: n.deviceId,
                  name: n.deviceName || n.deviceId,
                  batteryLevel: n.batteryLevel ?? null,
                  locationName: n.locationName ?? null,
                }));
              return [...offlineRows, ...lowBatteryRows].slice(0, remaining).map((row) => (
                <div
                  key={row.deviceId}
                  className="flex items-start justify-between p-2 rounded border border-red-200 bg-red-50 text-sm"
                  data-testid={`lock-issue-row-${row.deviceId}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {row.kind === "offline" ? (
                        <WifiOff className="h-3.5 w-3.5 text-red-500 shrink-0" />
                      ) : (
                        <BatteryWarning className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                      )}
                      <span className="truncate font-medium text-gray-900">{row.name}</span>
                    </div>
                    {row.locationName && row.locationName !== row.name && (
                      <p className="text-xs text-gray-500 mt-0.5 ml-5">{row.locationName}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0 ml-2">
                    {row.kind === "offline" && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Offline</Badge>
                    )}
                    {row.kind === "low_battery" && row.batteryLevel != null && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50">
                        {row.batteryLevel}% battery
                      </Badge>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}
    </div>
  );
}

interface ListingReminder {
  id: number;
  platform: string;
  url: string;
  propertyId: number | null;
  reminderDate: string;
  notes: string | null;
}

const ACQ_STATUS_LABELS: Record<string, string> = {
  prospect: "Prospect",
  offer_submitted: "Offer Submitted",
  offer_accepted: "Offer Accepted",
  under_contract: "Under Contract",
  due_diligence: "Due Diligence",
  appraisal: "Appraisal",
  clear_to_close: "Clear to Close",
  closing: "Closing",
  closed: "Closed",
};

const ACQ_STATUS_COLORS: Record<string, string> = {
  prospect: "bg-gray-100 text-gray-700",
  offer_submitted: "bg-blue-100 text-blue-700",
  offer_accepted: "bg-indigo-100 text-indigo-700",
  under_contract: "bg-purple-100 text-purple-700",
  due_diligence: "bg-yellow-100 text-yellow-700",
  appraisal: "bg-orange-100 text-orange-700",
  clear_to_close: "bg-teal-100 text-teal-700",
  closing: "bg-emerald-100 text-emerald-700",
  closed: "bg-green-100 text-green-700",
};

function getNextDates(acq: Acquisition): { label: string; date: string }[] {
  const now = new Date();
  const dateFields: { key: keyof Acquisition; label: string }[] = [
    { key: "offerExpirationDate", label: "Offer Expires" },
    { key: "optionPeriodExpiration", label: "Option Expires" },
    { key: "contractDate", label: "Contract" },
    { key: "inspectionDeadline", label: "Inspection" },
    { key: "appraisalDeadline", label: "Appraisal" },
    { key: "financingContingencyDeadline", label: "Financing" },
    { key: "closingDateScheduled", label: "Closing" },
  ];
  return dateFields
    .filter((f) => {
      const val = acq[f.key] as string | null | undefined;
      return val && new Date(val) >= now;
    })
    .sort((a, b) => new Date(acq[a.key] as string).getTime() - new Date(acq[b.key] as string).getTime())
    .slice(0, 2)
    .map((f) => ({ label: f.label, date: acq[f.key] as string }));
}

function AcquisitionsPanel() {
  const acqQuery = useQuery<Acquisition[]>({ queryKey: ["/api/app/acquisitions"] });
  const acquisitions = (acqQuery.data || []).filter((a) => a.status !== "closed").slice(0, 5);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-acquisitions">
      <PanelHeader title="Acquisitions" href="/app/acquisitions" icon={ShoppingCart} />
      {acqQuery.isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : acquisitions.length === 0 ? (
        <EmptyState message="No active acquisitions" />
      ) : (
        <div className="space-y-2">
          {acquisitions.map((acq) => {
            const nextDates = getNextDates(acq);
            return (
              <Link key={acq.id} href={`/app/acquisitions/${acq.id}`}>
                <div className="p-2.5 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer" data-testid={`acquisition-row-${acq.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-gray-900 truncate flex-1 mr-2">{acq.address}</p>
                    <Badge className={`${ACQ_STATUS_COLORS[acq.status] || "bg-gray-100 text-gray-700"} border-0 text-[10px] shrink-0`}>
                      {ACQ_STATUS_LABELS[acq.status] || acq.status}
                    </Badge>
                  </div>
                  {nextDates.length > 0 ? (
                    <div className="flex gap-3 mt-1">
                      {nextDates.map((nd) => {
                        const days = differenceInDays(new Date(nd.date), new Date());
                        const isUrgent = days <= 7;
                        return (
                          <span key={nd.label} className={`text-xs ${isUrgent ? "text-orange-600 font-medium" : "text-gray-500"}`}>
                            {nd.label}: {format(new Date(nd.date), "MMM d")} {isUrgent && `(${days}d)`}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">No upcoming dates</p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ListingRemindersPanel({ activePropertyIds, metric = false }: { activePropertyIds: Set<number> | null; metric?: boolean }) {
  const remindersQuery = useQuery<ListingReminder[]>({
    queryKey: ["/api/app/marketing/listing-reminders"],
    refetchInterval: metric ? 60_000 : false,
  });

  const allReminders = remindersQuery.data || [];
  const reminders = useMemo(() => {
    if (!activePropertyIds) return allReminders;
    return allReminders.filter(r => r.propertyId != null && activePropertyIds.has(r.propertyId));
  }, [allReminders, activePropertyIds]);

  if (metric) return <MetricCard label="Listing Reminders" icon={Bell}
    href="/app/marketing/third-party-listings"
    value={remindersQuery.isError ? "Unavailable" : remindersQuery.isLoading ? "—" : reminders.length} />;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="panel-listing-reminders">
      <PanelHeader title="Listing Reminders" href="/app/marketing/third-party-listings" icon={Bell} />
      {remindersQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : reminders.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-6">No upcoming listing reminders</p>
      ) : (
        <div className="space-y-2">
          {reminders.slice(0, 5).map((r) => {
            const daysUntil = Math.ceil((new Date(r.reminderDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            const isUrgent = daysUntil <= 3;
            return (
              <a
                key={r.id}
                href={`/app/marketing/third-party-listings?edit=${r.id}`}
                className="flex items-center gap-3 p-2 border border-gray-100 rounded-lg hover:bg-gray-50 hover:border-gray-200 transition-colors cursor-pointer"
                data-testid={`listing-reminder-${r.id}`}
              >
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-bold ${isUrgent ? "bg-red-500" : "bg-amber-500"}`}>
                  {r.platform.substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{r.platform.replace(/_/g, " ")}</p>
                  <p className="text-xs text-gray-400">{format(new Date(r.reminderDate), "MMM d, yyyy")}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${isUrgent ? "border-red-200 text-red-700 bg-red-50" : "border-amber-200 text-amber-700 bg-amber-50"}`}>
                  {daysUntil <= 0 ? "Today" : daysUntil === 1 ? "Tomorrow" : `${daysUntil}d`}
                </Badge>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BuyBoxReportsPanel() {
  const reportsQuery = useQuery<Array<{ id: number; city: string; state: string; status: string; totalFound: number; totalPassed: number; startedAt: string; queuePosition?: number | null; queueEstimatedWaitSeconds?: number | null }>>({
    queryKey: ["/api/app/buy-box/reports/recent"],
    queryFn: async () => {
      const res = await fetch("/api/app/buy-box/reports/recent?limit=3", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    refetchInterval: (query) => {
      const data = query.state.data as Array<{ status: string }> | undefined;
      if (data && data.some(r => r.status === "queued" || r.status === "running")) return 5000;
      return false;
    },
  });
  const reports = reportsQuery.data || [];

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="panel-buy-box-reports">
      <PanelHeader title="Buy Box Reports" href="/app/buy-box/reports" icon={Target} />
      {reportsQuery.isLoading ? (
        <div className="space-y-2 mt-2">{[1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : reports.length === 0 ? (
        <EmptyState message="No scans run yet. Visit a city in Research to scan Zillow." />
      ) : (
        <div className="space-y-2 mt-2">
          {reports.map(report => (
            <Link key={report.id} href={`/app/buy-box/reports/${report.id}`}>
              <div className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer" data-testid={`dashboard-report-${report.id}`}>
                <div className="flex items-center gap-2">
                  {report.status === "queued"
                    ? <Clock className="h-4 w-4 text-gray-400 animate-pulse" />
                    : report.status === "running"
                      ? <Clock className="h-4 w-4 text-blue-500 animate-pulse" />
                      : report.status === "complete" || report.status === "partially_complete"
                        ? <CheckCircle className="h-4 w-4 text-green-500" />
                        : <FileText className="h-4 w-4 text-gray-400" />}
                  {report.status === "queued"
                    ? (
                      <span className="text-sm font-medium text-gray-600" data-testid={`text-queued-${report.id}`}>
                        Queued: {report.city}
                        {report.queuePosition && report.queuePosition > 0 ? (
                          <span className="text-xs text-gray-500 ml-1" data-testid={`text-queue-position-${report.id}`}>
                            {report.queuePosition === 1
                              ? "(next up)"
                              : `(${report.queuePosition - 1} ahead)`}
                          </span>
                        ) : "…"}
                      </span>
                    )
                    : report.status === "running"
                      ? <span className="text-sm font-medium text-blue-600 animate-pulse" data-testid={`text-analyzing-${report.id}`}>Analyzing {report.city}…</span>
                      : <span className="text-sm font-medium text-gray-800">{report.city}, {report.state}</span>}
                </div>
                <div className="text-right">
                  {report.status !== "running" && report.status !== "queued" && (
                    <>
                      <span className="text-sm font-semibold text-green-700">{report.totalPassed}</span>
                      <span className="text-xs text-gray-400">/{report.totalFound} pass</span>
                    </>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Unified Team Inbox dashboard panel ───────────────────────────────────────
// Replaces the old "Unresolved Messages" panel. Shows a single org-scoped queue
// that unifies inbound member messages (resolved note-free via the existing
// /api/app/messages/:id/resolve|unresolve endpoints) with team_inbox_events
// (vendor messages, etc., resolved via /api/app/team-inbox/events/:id/*). The
// bell notification feed is unchanged — this is a separate shared surface.

const TEAM_INBOX_REFETCH_INTERVAL_MS = 22_000;

type TeamInboxItem = {
  key: string;
  kind: "member_message" | "event" | "todo";
  id: number;
  eventType: string;
  title: string;
  preview: string | null;
  sourceLabel: string | null;
  link: string | null;
  memberId: number | null;
  memberName: string | null;
  propertyId: number | null;
  propertyName: string | null;
  roomName: string | null;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: number | null;
  resolvedByName: string | null;
  resolveNote: string | null;
  assignedTo: number | null;
  assignedToName: string | null;
  assignedAt: string | null;
  severity: "normal" | "critical";
  autoResolved: boolean;
  dueAt: string | null;
  attemptCount: number | null;
  lastFailedAt: string | null;
};
type TeamInboxResponse = { items: TeamInboxItem[]; openCount: number; assignedToMeCount?: number };
type TeamInboxTab = "needs_attention" | "resolved" | "all";
type TeamInboxAssignment = "all" | "mine" | "unassigned";

function teamInboxEventTypeLabel(t: string): string {
  switch (t) {
    case "member_message": return "Message";
    case "vendor_message": return "Vendor";
    case "service_form": return "Form";
    case "supply_order": return "Supply";
    case "vendor_task_update": return "Task";
    case "booking_request": return "Booking";
    case "maintenance": return "Maintenance";
    case "mercury_statement": return "Statement";
    case "closing_statement": return "Closing";
    case "padsplit_push_failure": return "PadSplit";
    case "cleaning_proof_overdue":
    case "cleaning_uncovered_slot": return "Cleaning";
    case "broadcast_delivery_incomplete":
    case "broadcast_roster_gap": return "Broadcast";
    case "todo": return "To-Do";
    default: return t;
  }
}

// Tick once per minute to update live age timers.
function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function formatAgeMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function ageColor(ageMs: number): string {
  const h = ageMs / 3_600_000;
  if (h < 1) return "text-green-600";
  if (h < 4) return "text-amber-600";
  return "text-red-600";
}

// Local "today" (host-browser tz) as YYYY-MM-DD for comparing a to-do's
// date-only due_date. A to-do is overdue once its due date is strictly before
// today — matches the server's CURRENT_DATE-based overdue band.
function localTodayISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}
function isOverdueTodoDate(dueAt: string | null): boolean {
  if (!dueAt) return false;
  return dueAt.slice(0, 10) < localTodayISO();
}

function ResolveNoteForm({
  disabled,
  onSubmit,
  testIdBase,
}: {
  disabled?: boolean;
  onSubmit: (note: string) => void;
  testIdBase: string;
}) {
  const [note, setNote] = useState("");
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium">Resolve with a note</p>
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Add a note (optional)"
        className="text-xs min-h-[60px]"
        data-testid={`team-inbox-note-input-${testIdBase}`}
      />
      <Button
        size="sm"
        className="w-full h-7 text-xs"
        disabled={disabled}
        onClick={() => onSubmit(note)}
        data-testid={`team-inbox-note-resolve-${testIdBase}`}
      >
        Resolve
      </Button>
    </div>
  );
}

export function TeamInboxPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const now = useNow();
  const [tab, setTab] = useState<TeamInboxTab>("needs_attention");
  // Client-side type filter over the loaded rows, keyed by the same badge
  // label each row displays (Message, Vendor, Form, To-Do, …).
  const [typeFilter, setTypeFilter] = useState<string>("all");
  // Task #3971 — On the first dashboard load right after onboarding, default the
  // inbox to "Assigned to me" so the freshly-seeded setup checklist is front and
  // center. The one-shot signal is consumed (read + cleared) exactly once, so
  // every later visit falls back to the normal "all" default. Lazy initializer
  // keeps the consume out of render re-runs.
  const [assignment, setAssignment] = useState<TeamInboxAssignment>(
    () => (consumeOnboardingJustCompleted() ? "mine" : "all"),
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkNote, setBulkNote] = useState("");

  // Task #3972 — Deep-link target consumed from `?inbox=<kind>:<id>` (see the
  // effects below). `pendingInbox` is the parsed target awaiting a matching
  // loaded row; `flashKey` is the resolved item key currently flashing its ring.
  const search = useSearch();
  const [panelLocation, navigatePanel] = useLocation();
  const [pendingInbox, setPendingInbox] = useState<InboxTarget | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLElement>>(new Map());
  // Task #3982 — fetch-by-target fallback. When the deep-link target is older
  // than the first loaded page it won't be in `items`; we then fetch that one
  // item by id and splice it in so it can scroll + flash. `fallbackFetchedFor`
  // guards against re-firing the fetch for the same target.
  const [fallbackItem, setFallbackItem] = useState<TeamInboxItem | null>(null);
  const fallbackFetchedFor = useRef<string | null>(null);

  const statusParam = tab === "resolved" ? "resolved" : tab === "all" ? "all" : "open";
  // Keep the default view (Needs attention + All) on the 2-element
  // ["/api/app/team-inbox","open"] key so the optimistic cache update lines up;
  // only append the assignment segment once it actually narrows the queue.
  const queryKey = assignment === "all"
    ? ["/api/app/team-inbox", statusParam]
    : ["/api/app/team-inbox", statusParam, assignment];

  const query = useQuery<TeamInboxResponse>({
    queryKey,
    queryFn: () =>
      apiRequest("GET", `/api/app/team-inbox?status=${statusParam}&assignment=${assignment}`).then((r) => r.json()),
    refetchInterval: TEAM_INBOX_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  // Owner + accepted teammates power the per-item assign menu. Uses the shared
  // default fetcher (like the messages page) so it stays an array; guard the
  // non-array shape some unit tests stub.
  const teamQuery = useQuery<TeamMember[]>({ queryKey: ["/api/app/team-members"] });
  const assignees = (Array.isArray(teamQuery.data) ? teamQuery.data : [])
    .filter((m) => m.status === "accepted")
    .map((m) => ({ id: m.id === 0 ? m.userId : m.inviteeUserId, name: m.name || m.email || "Teammate" }))
    .filter((a): a is { id: number; name: string } => a.id != null);

  // Reopen / unresolve (used by the undo toast and the Resolved/All tabs).
  const reopenMutation = useMutation({
    mutationFn: ({ item }: { item: TeamInboxItem }) =>
      item.kind === "member_message"
        ? apiRequest("POST", `/api/app/messages/${item.id}/unresolve`, {}).then((r) => r.json())
        : item.kind === "todo"
        ? apiRequest("POST", `/api/app/todos/${item.id}/reopen`, {}).then((r) => r.json())
        : apiRequest("POST", `/api/app/team-inbox/events/${item.id}/reopen`, {}).then((r) => r.json()),
    onSettled: (_data, _err, { item }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-inbox"] });
      if (item.kind === "member_message" && item.memberId != null) {
        invalidateResolveQueries(queryClient, item.memberId);
      }
    },
  });

  // Resolve one item with optimistic removal + rollback. Member messages stay
  // note-free via the existing endpoint; events accept an optional note.
  const resolveMutation = useMutation({
    mutationFn: ({ item, note }: { item: TeamInboxItem; note?: string }) =>
      item.kind === "member_message"
        ? apiRequest("POST", `/api/app/messages/${item.id}/resolve`, {}).then((r) => r.json())
        : item.kind === "todo"
        ? apiRequest("POST", `/api/app/todos/${item.id}/resolve`, {}).then((r) => r.json())
        : apiRequest(
            "POST",
            `/api/app/team-inbox/events/${item.id}/resolve`,
            note && note.trim() ? { note: note.trim() } : {},
          ).then((r) => r.json()),
    onMutate: async ({ item }) => {
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<TeamInboxResponse>(queryKey);
      queryClient.setQueryData<TeamInboxResponse>(queryKey, (old) =>
        old
          ? {
              ...old,
              items: old.items.filter((i) => i.key !== item.key),
              openCount: item.resolvedAt == null ? Math.max(0, old.openCount - 1) : old.openCount,
            }
          : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(queryKey, ctx.prev);
      toast({ title: "Could not resolve item", variant: "destructive" });
    },
    onSuccess: (_data, { item }) => {
      toast({
        title: "Resolved",
        description: item.title,
        action: (
          <button
            className="text-xs underline underline-offset-2 text-brand hover:opacity-80"
            onClick={() => reopenMutation.mutate({ item })}
            data-testid={`button-undo-resolve-${item.key}`}
          >
            Undo
          </button>
        ) as any,
        duration: 5000,
      });
    },
    onSettled: (_data, _err, { item }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-inbox"] });
      if (item.kind === "member_message" && item.memberId != null) {
        invalidateResolveQueries(queryClient, item.memberId);
      }
    },
  });

  // Assign / unassign an item to the owner or an accepted teammate.
  const assignMutation = useMutation({
    mutationFn: ({ item, assigneeId }: { item: TeamInboxItem; assigneeId: number | "me" | null }) =>
      apiRequest(
        "POST",
        `/api/app/team-inbox/${item.kind === "member_message" ? "message" : item.kind === "todo" ? "todo" : "event"}/${item.id}/assign`,
        { assigneeId },
      ).then((r) => r.json()),
    onError: () => toast({ title: "Could not update assignment", variant: "destructive" }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["/api/app/team-inbox"] }),
  });

  // Resolve a mixed batch of selected items in one round-trip.
  const bulkResolveMutation = useMutation({
    mutationFn: ({ items: batch, note }: { items: TeamInboxItem[]; note?: string }) =>
      apiRequest("POST", "/api/app/team-inbox/bulk-resolve", {
        items: batch.map((i) => ({ kind: i.kind, id: i.id })),
        ...(note && note.trim() ? { note: note.trim() } : {}),
      }).then((r) => r.json()),
    onSuccess: (data) => {
      const n = Number(data?.resolved ?? 0);
      toast({ title: `Resolved ${n} item${n === 1 ? "" : "s"}` });
      setSelected(new Set());
      setBulkNote("");
    },
    onError: () => toast({ title: "Could not resolve items", variant: "destructive" }),
    onSettled: (_data, _err, { items: batch }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-inbox"] });
      for (const it of batch) {
        if (it.kind === "member_message" && it.memberId != null) {
          invalidateResolveQueries(queryClient, it.memberId);
        }
      }
    },
  });

  const items = query.data?.items ?? [];
  // Splice in the fetch-by-target fallback row (Task #3982) when it isn't already
  // part of the loaded page, so a deep link to an off-page item still renders +
  // flashes. Deduped by key so a later refetch that includes it won't double it.
  const displayItems =
    fallbackItem && !items.some((i) => i.key === fallbackItem.key)
      ? [fallbackItem, ...items]
      : items;
  // Badge label for a row — the same string rendered on the row itself, so
  // the type chips can never disagree with what the user sees.
  const itemTypeLabel = (item: TeamInboxItem) =>
    item.kind === "todo" ? "To-Do" : teamInboxEventTypeLabel(item.eventType);
  const availableTypeLabels = Array.from(new Set(displayItems.map(itemTypeLabel))).sort();
  const filteredItems =
    typeFilter === "all" ? displayItems : displayItems.filter((i) => itemTypeLabel(i) === typeFilter);

  const openCount = query.data?.openCount ?? 0;
  const assignedToMeCount = query.data?.assignedToMeCount ?? 0;
  // The server caps the open list (LIMIT 200) while the count badge above is an
  // uncapped total, so for a host with a big backlog the visible rows can be
  // fewer than the badge number. Surface the remainder as an explicit "+N more"
  // so the badge and the list never read as a mismatch. Only meaningful on the
  // unfiltered open view — the count badge is the global open total, so the
  // gap is only apples-to-apples when no assignment filter is narrowing the
  // rows. (Task #4138)
  const visibleOpenCount = displayItems.filter((i) => i.resolvedAt == null).length;
  const hiddenOpenCount =
    assignment === "all" ? Math.max(0, openCount - visibleOpenCount) : 0;
  const busy =
    resolveMutation.isPending ||
    reopenMutation.isPending ||
    assignMutation.isPending ||
    bulkResolveMutation.isPending;

  // Task #3972 — Deep-link consumption. The assignment-notification dispatcher
  // links recipients to `/app/dashboard?inbox=<kind>:<id>`. Capture that param
  // once, widen the view (all statuses, all assignees) so the target can
  // surface, then strip the query so a later refetch/refresh can't re-trigger
  // the scroll.
  useEffect(() => {
    const target = parseInboxParam(search);
    if (!target) return;
    setPendingInbox(target);
    // Reset any prior fallback so a new target re-fetches cleanly.
    setFallbackItem(null);
    fallbackFetchedFor.current = null;
    setTab("all");
    setAssignment("all");
    setTypeFilter("all");
    navigatePanel(panelLocation, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Once the target row is loaded (either in the page or spliced in via the
  // fetch-by-target fallback below), scroll to it and flash a ring.
  useEffect(() => {
    if (!pendingInbox) return;
    const key = resolveInboxTargetKey(displayItems, pendingInbox);
    if (!key) return;
    const el = rowRefs.current.get(key);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    setFlashKey(key);
    setPendingInbox(null);
  }, [pendingInbox, displayItems]);

  // Fetch-by-target fallback: when the deep-link target isn't in the loaded page
  // (older than the first ~200 rows), fetch that one item by id and splice it in
  // so the scroll/flash effect above can find it. Org-scoped server-side.
  useEffect(() => {
    if (!pendingInbox) return;
    if (query.isLoading) return;
    // Already present in the loaded page — no fallback needed.
    if (resolveInboxTargetKey(items, pendingInbox)) return;
    const targetStr = `${pendingInbox.kind}:${pendingInbox.id}`;
    if (fallbackFetchedFor.current === targetStr) return;
    fallbackFetchedFor.current = targetStr;
    let cancelled = false;
    apiRequest("GET", `/api/app/team-inbox/item?kind=${pendingInbox.kind}&id=${pendingInbox.id}`)
      .then((r) => r.json())
      .then((data: { item?: TeamInboxItem | null }) => {
        if (cancelled) return;
        if (data?.item) setFallbackItem(data.item);
      })
      .catch(() => {
        // Network/permission failure — leave the panel on the list; the user
        // can still find the item manually.
      });
    return () => {
      cancelled = true;
    };
  }, [pendingInbox, items, query.isLoading]);

  // Auto-clear the flash ring after a few seconds.
  useEffect(() => {
    if (!flashKey) return;
    const t = setTimeout(() => setFlashKey(null), 3000);
    return () => clearTimeout(t);
  }, [flashKey]);

  // Only OPEN rows are selectable / resolvable. The "all" tab mixes open and
  // resolved rows, so gate select + resolve controls per row on resolvedAt (not
  // on the tab) — otherwise resolving an already-resolved row optimistically
  // drops it and decrements openCount until the refetch heals it.
  const selectableKeys = filteredItems.filter((i) => i.resolvedAt == null).map((i) => i.key);
  const selectedItems = filteredItems.filter((i) => i.resolvedAt == null && selected.has(i.key));
  const allVisibleSelected =
    selectableKeys.length > 0 && selectedItems.length === selectableKeys.length;
  const toggleSelect = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleSelectAll = () =>
    setSelected(allVisibleSelected ? new Set<string>() : new Set(selectableKeys));

  // Task #3982 — The fetch-by-target fallback row is only valid for the deep-link
  // landing view (forced to All/All by the consume effect). The moment the user
  // manually changes the tab or assignment filter, that spliced row may no longer
  // belong to the now-active view (e.g. an open item lingering on the Resolved
  // tab), so drop it and let the server query own the list again. Programmatic
  // deep-link setTab/setAssignment calls don't go through these handlers, so they
  // never clear a freshly-fetched fallback.
  const clearFallbackOnFilterChange = () => {
    setFallbackItem(null);
    fallbackFetchedFor.current = null;
  };
  const handleTabChange = (id: TeamInboxTab) => {
    clearFallbackOnFilterChange();
    setTab(id);
  };
  const handleAssignmentChange = (id: TeamInboxAssignment) => {
    clearFallbackOnFilterChange();
    setAssignment(id);
  };

  const isOpenTab = tab === "needs_attention";
  // "All" was dropped from the visible tabs — an item either needs attention
  // or it's resolved. The TeamInboxTab type keeps "all" because deep-links
  // still widen to it internally so any target row can surface.
  const TABS: { id: TeamInboxTab; label: string }[] = [
    { id: "needs_attention", label: "Needs attention" },
    { id: "resolved", label: "Resolved" },
  ];
  const ASSIGN_FILTERS: { id: TeamInboxAssignment; label: string }[] = [
    { id: "all", label: "All" },
    { id: "mine", label: `Assigned to me${assignedToMeCount ? ` (${assignedToMeCount})` : ""}` },
    { id: "unassigned", label: "Unassigned" },
  ];

  return (
    <div
      className="border border-gray-200 rounded-lg bg-white flex flex-col max-h-[50vh]"
      data-testid="panel-team-inbox"
    >
      {/* Fixed header: title, tabs, assignment filter, and bulk bar. The body
          below scrolls independently so these controls stay put. */}
      <div className="shrink-0 border-b border-gray-100 p-4 space-y-3">
        <PanelHeader
          title="Team Inbox"
          href="/app/messages"
          icon={MessageSquare}
          count={openCount > 0 ? openCount : undefined}
        />
        <div className="flex flex-wrap items-center gap-3">
          {/* Plain (non-Radix) tab buttons to keep jsdom tests deterministic. */}
          <div className="flex gap-1" data-testid="team-inbox-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => handleTabChange(t.id)}
                className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                  tab === t.id ? "bg-brand text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`team-inbox-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Divider — status on the left, assignment on the right. */}
          <span className="h-4 w-px bg-gray-300" aria-hidden="true" data-testid="team-inbox-filter-divider" />
          <div className="flex gap-1" data-testid="team-inbox-assignment-filter">
            {ASSIGN_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => handleAssignmentChange(f.id)}
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                  assignment === f.id
                    ? "border-brand text-brand bg-brand-muted"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
                data-testid={`team-inbox-assignment-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        {/* Type chips — filter by the same badge each row shows (Message,
            Vendor, Form, To-Do, …). Only rendered when the loaded rows span
            more than one type. */}
        {availableTypeLabels.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="team-inbox-type-filter">
            <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mr-0.5">Type</span>
            <button
              type="button"
              onClick={() => setTypeFilter("all")}
              className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                typeFilter === "all"
                  ? "border-brand text-brand bg-brand-muted"
                  : "border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
              data-testid="team-inbox-type-all"
            >
              All types
            </button>
            {availableTypeLabels.map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => setTypeFilter((cur) => (cur === label ? "all" : label))}
                className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                  typeFilter === label
                    ? "border-brand text-brand bg-brand-muted"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
                data-testid={`team-inbox-type-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {selectableKeys.length > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={allVisibleSelected}
              onCheckedChange={() => toggleSelectAll()}
              aria-label="Select all"
              data-testid="team-inbox-select-all"
            />
            <span className="text-xs text-muted-foreground">
              {selectedItems.length > 0 ? `${selectedItems.length} selected` : "Select all"}
            </span>
            {selectedItems.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    size="sm"
                    className="h-7 px-2 text-xs ml-auto"
                    disabled={busy}
                    data-testid="team-inbox-bulk-resolve"
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Resolve {selectedItems.length}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 space-y-2">
                  <p className="text-xs font-medium">
                    Resolve {selectedItems.length} item{selectedItems.length === 1 ? "" : "s"}
                  </p>
                  <Textarea
                    value={bulkNote}
                    onChange={(e) => setBulkNote(e.target.value)}
                    placeholder="Optional note (events only)"
                    className="text-xs min-h-[60px]"
                    data-testid="team-inbox-bulk-note"
                  />
                  <Button
                    size="sm"
                    className="w-full h-7 text-xs"
                    disabled={busy}
                    onClick={() => bulkResolveMutation.mutate({ items: selectedItems, note: bulkNote })}
                    data-testid="team-inbox-bulk-resolve-confirm"
                  >
                    Resolve
                  </Button>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {query.isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        )}
        {!query.isLoading && displayItems.length === 0 && (
          <EmptyState
            message={
              isOpenTab
                ? "All caught up — nothing needs attention."
                : tab === "resolved"
                  ? "No resolved items yet."
                  : "No items."
            }
          />
        )}
        <div className="space-y-2">
          {filteredItems.map((item) => {
            const ageMs = now.getTime() - new Date(item.createdAt).getTime();
            const checked = selected.has(item.key);
            const itemResolved = item.resolvedAt != null;
            const todoOverdue = item.kind === "todo" && isOverdueTodoDate(item.dueAt);
            const rowEmphasisRed = !itemResolved && (item.severity === "critical" || todoOverdue);
            return (
              <div
                key={item.key}
                ref={(el) => {
                  if (el) rowRefs.current.set(item.key, el);
                  else rowRefs.current.delete(item.key);
                }}
                className={`flex items-start gap-2 px-3 py-2 rounded-lg border bg-white scroll-mt-4 transition-shadow ${
                  rowEmphasisRed ? "border-red-300 bg-red-50/40" : "border-gray-200"
                } ${flashKey === item.key ? "ring-2 ring-brand ring-offset-2" : ""}`}
                data-testid={`team-inbox-item-${item.key}`}
              >
                {!itemResolved && (
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleSelect(item.key)}
                    aria-label="Select item"
                    className="mt-1 shrink-0"
                    data-testid={`team-inbox-select-${item.key}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {item.link ? (
                      <Link
                        href={item.link}
                        className="font-medium text-sm text-brand hover:underline truncate"
                        data-testid={`team-inbox-link-${item.key}`}
                      >
                        {item.title}
                      </Link>
                    ) : (
                      <span className="font-medium text-sm truncate">{item.title}</span>
                    )}
                    <Badge variant="secondary" className="text-[10px] h-4 px-1.5 shrink-0">
                      {teamInboxEventTypeLabel(item.eventType)}
                    </Badge>
                    {item.severity === "critical" && !itemResolved && (
                      <Badge
                        variant="destructive"
                        className="text-[10px] h-4 px-1.5 shrink-0"
                        data-testid={`team-inbox-critical-${item.key}`}
                      >
                        Critical
                      </Badge>
                    )}
                    {item.kind === "todo" && item.dueAt && !itemResolved && (
                      todoOverdue ? (
                        <Badge
                          variant="destructive"
                          className="text-[10px] h-4 px-1.5 shrink-0"
                          data-testid={`team-inbox-overdue-${item.key}`}
                        >
                          Overdue
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-[10px] h-4 px-1.5 shrink-0"
                          data-testid={`team-inbox-due-${item.key}`}
                        >
                          Due {item.dueAt.slice(0, 10)}
                        </Badge>
                      )
                    )}
                    {item.assignedToName && (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-4 px-1.5 shrink-0"
                        data-testid={`team-inbox-assignee-${item.key}`}
                      >
                        {item.assignedToName}
                      </Badge>
                    )}
                  </div>
                  {item.sourceLabel && (
                    <p className="text-[11px] text-muted-foreground truncate">{item.sourceLabel}</p>
                  )}
                  {item.preview && <p className="text-xs text-gray-700 truncate">{item.preview}</p>}
                  {item.eventType === "padsplit_push_failure" &&
                    item.attemptCount != null &&
                    item.attemptCount > 1 &&
                    !itemResolved && (
                      <p
                        className="text-[11px] text-red-600 truncate"
                        data-testid={`team-inbox-attempts-${item.key}`}
                      >
                        Failed {item.attemptCount} times
                      </p>
                    )}
                  {itemResolved && (item.resolvedByName || item.resolveNote) && (
                    <p
                      className="text-[11px] text-muted-foreground truncate"
                      data-testid={`team-inbox-resolved-meta-${item.key}`}
                    >
                      Resolved{item.resolvedByName ? ` by ${item.resolvedByName}` : ""}
                      {item.resolveNote ? ` — \u201C${item.resolveNote}\u201D` : ""}
                    </p>
                  )}
                </div>
                <span
                  className={`text-[10px] font-medium shrink-0 tabular-nums ${
                    !itemResolved ? ageColor(ageMs) : "text-muted-foreground"
                  }`}
                  data-testid={`team-inbox-age-${item.key}`}
                >
                  {formatAgeMs(ageMs)}
                </span>
                {!itemResolved ? (
                  <>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="shrink-0 h-5 px-1 text-[11px] text-gray-400 hover:text-brand"
                          disabled={busy}
                          title="Assign"
                          data-testid={`team-inbox-assign-${item.key}`}
                        >
                          Assign
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-48 p-1">
                        <button
                          type="button"
                          className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-gray-100"
                          onClick={() => assignMutation.mutate({ item, assigneeId: "me" })}
                          data-testid={`team-inbox-assign-me-${item.key}`}
                        >
                          Assign to me
                        </button>
                        {assignees.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-gray-100 truncate"
                            onClick={() => assignMutation.mutate({ item, assigneeId: a.id })}
                            data-testid={`team-inbox-assign-${a.id}-${item.key}`}
                          >
                            {a.name}
                          </button>
                        ))}
                        {item.assignedTo != null && (
                          <button
                            type="button"
                            className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-gray-100 text-red-600"
                            onClick={() => assignMutation.mutate({ item, assigneeId: null })}
                            data-testid={`team-inbox-unassign-${item.key}`}
                          >
                            Unassign
                          </button>
                        )}
                      </PopoverContent>
                    </Popover>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="shrink-0 h-5 w-5 p-0 text-gray-400 hover:text-green-600"
                      disabled={busy}
                      title="Mark as resolved"
                      onClick={() => resolveMutation.mutate({ item })}
                      data-testid={`team-inbox-resolve-${item.key}`}
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                    {item.kind === "event" && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="shrink-0 h-5 w-5 p-0 text-gray-400 hover:text-brand"
                            disabled={busy}
                            title="Resolve with note"
                            data-testid={`team-inbox-resolve-note-${item.key}`}
                          >
                            <FileText className="h-3 w-3" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-64 space-y-2">
                          <ResolveNoteForm
                            disabled={busy}
                            onSubmit={(note) => resolveMutation.mutate({ item, note })}
                            testIdBase={item.key}
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="shrink-0 h-6 px-2 text-xs text-gray-400 hover:text-brand"
                    disabled={busy}
                    title="Reopen"
                    onClick={() => reopenMutation.mutate({ item })}
                    data-testid={`team-inbox-reopen-${item.key}`}
                  >
                    Reopen
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        {isOpenTab && (hiddenOpenCount > 0 || (assignment !== "all" && items.length >= 200)) && (
          <Link
            href="/app/messages"
            className="block text-center text-xs text-brand hover:underline pt-2"
            data-testid="team-inbox-view-all-link"
          >
            {hiddenOpenCount > 0 ? `+${hiddenOpenCount} more — View all →` : "View all →"}
          </Link>
        )}
      </div>
    </div>
  );
}

const DEFAULT_PANEL_ORDER = [
  "maintenance",
  "approvals",
  "buyBoxReports",
  "connections",
];

const PANEL_LABELS: Record<string, string> = {
  connections: "Connections",
  approvals: "Pending Vendor Payments",
  buyBoxReports: "Buy Box Reports",
  maintenance: "Open Tasks",
};

interface SortableWidgetProps {
  id: string;
  isEditMode: boolean;
  size: PanelSize;
  onSizeChange?: (id: string, size: PanelSize) => void;
  onHide?: (id: string) => void;
  children: React.ReactNode;
}

const SIZE_CLASSES: Record<PanelSize, string> = {
  default: "col-span-2",
  wide: "col-span-2 md:col-span-4",
  tall: "col-span-2 row-span-2",
  half: "col-span-1",
  "half-tall": "col-span-1 row-span-2",
};

function SortableWidget({ id, isEditMode, size, onSizeChange, onHide, children }: SortableWidgetProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isEditMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  function cycleSize() {
    if (!onSizeChange) return;
    const sizes: PanelSize[] = ["half", "half-tall", "default", "wide", "tall"];
    const nextIdx = (sizes.indexOf(size) + 1) % sizes.length;
    onSizeChange(id, sizes[nextIdx]);
  }

  const sizeLabel: Record<PanelSize, string> = {
    half: "0.5x",
    "half-tall": "0.5x tall",
    default: "1x",
    wide: "2x",
    tall: "tall",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${SIZE_CLASSES[size]} relative ${isEditMode ? "rounded-xl ring-2 ring-blue-300 ring-offset-1" : ""} ${(isEditMode || size === "tall" || size === "half-tall") ? "[&>div]:h-full" : ""}`}
      data-testid={`sortable-widget-${id}`}
    >
      {isEditMode && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
          <button
            type="button"
            onClick={cycleSize}
            className="bg-white border border-gray-200 rounded-md p-1 shadow-sm hover:bg-gray-50 flex items-center gap-1"
            title={`Size: ${size} (click to change)`}
            data-testid={`size-toggle-${id}`}
          >
            {size === "default" && <Square className="h-3.5 w-3.5 text-gray-500" />}
            {size === "wide" && <Columns className="h-3.5 w-3.5 text-blue-600" />}
            {size === "tall" && <Rows className="h-3.5 w-3.5 text-blue-600" />}
            {size === "half" && <Minimize2 className="h-3.5 w-3.5 text-orange-500" />}
            {size === "half-tall" && <Minimize2 className="h-3.5 w-3.5 text-purple-500" />}
            <span className="text-[10px] font-medium text-gray-500 pr-0.5">{sizeLabel[size]}</span>
          </button>
          <button
            type="button"
            onClick={() => onHide?.(id)}
            className="bg-white border border-gray-200 rounded-md p-1 shadow-sm hover:bg-red-50 hover:border-red-200"
            title="Hide this card"
            data-testid={`hide-panel-${id}`}
          >
            <EyeOff className="h-3.5 w-3.5 text-gray-400 hover:text-red-500" />
          </button>
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing bg-white border border-gray-200 rounded-md p-1 shadow-sm hover:bg-gray-50"
            data-testid={`drag-handle-${id}`}
          >
            <GripVertical className="h-4 w-4 text-gray-500" />
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

interface JourneyAction {
  label: string;
  href: string;
}

interface JourneyGuide {
  headline: string;
  description: string;
  actions: JourneyAction[];
}

const JOURNEY_GUIDES: Record<JourneyStage, JourneyGuide> = {
  searching: {
    headline: "Find your first CoLiving home",
    description: "Use research and buy box scans to surface deals that fit your criteria, then track promising leads as acquisitions.",
    actions: [
      { label: "Configure Buy Box", href: "/app/buy-box/settings" },
      { label: "Configure Underwriting", href: "/app/underwriting" },
      { label: "Browse Research", href: "/app/research" },
      { label: "Find a Lender", href: "/app/finance" },
    ],
  },
  under_contract: {
    headline: "Move your contract to closing",
    description: "Stay on top of contingency dates, underwriting, and the closing checklist while you finalize your first deal.",
    actions: [
      { label: "Open Acquisitions", href: "/app/acquisitions" },
      { label: "Run Underwriting", href: "/app/underwriting" },
      { label: "Plan the Launch", href: "/app/property-launch" },
    ],
  },
  renovating: {
    headline: "Get the house ready to launch",
    description: "Coordinate renovations, vendor approvals, and the launch checklist so you can flip the property to active without surprises.",
    actions: [
      { label: "Track Renovations", href: "/app/renovations" },
      { label: "Manage Vendors", href: "/app/operations/vendors" },
      { label: "Property Launch", href: "/app/property-launch" },
    ],
  },
  operating_self_managed: {
    headline: "Run your house like a pro",
    description: "Stay ahead of maintenance, member messages, and supply runs so day-to-day operations stay smooth.",
    actions: [
      { label: "Tasks", href: "/app/tasks" },
      { label: "Member Messages", href: "/app/messages" },
      { label: "Supply Orders", href: "/app/supplies" },
    ],
  },
  operating_with_pm: {
    headline: "Scale your portfolio with confidence",
    description: "With a PM handling day-to-day operations, focus on financial performance, vendor oversight, and finding your next acquisition.",
    actions: [
      { label: "Financial Overview", href: "/app/financials" },
      { label: "Vendor Approvals", href: "/app/operations/approvals" },
      { label: "Buy Property", href: "/app/buy" },
    ],
  },
};

function JourneyStageGuide({ journeyStage, isCardVisible }: { journeyStage: JourneyStage; isCardVisible: (href: string) => boolean }) {
  const queryClient = useQueryClient();
  const guide = JOURNEY_GUIDES[journeyStage];

  const dismissedQuery = useQuery<{ dismissedStages: string[] }>({
    queryKey: ["/api/app/dashboard/dismissed-journey-stages"],
  });

  const dismissMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: (stage: string) => apiRequest("POST", "/api/app/dashboard/dismissed-journey-stages", { stage }),
    onMutate: async (stage: string) => {
      await queryClient.cancelQueries({ queryKey: ["/api/app/dashboard/dismissed-journey-stages"] });
      const previous = queryClient.getQueryData<{ dismissedStages: string[] }>(["/api/app/dashboard/dismissed-journey-stages"]);
      queryClient.setQueryData<{ dismissedStages: string[] }>(
        ["/api/app/dashboard/dismissed-journey-stages"],
        (old) => ({ dismissedStages: [...(old?.dismissedStages ?? []), stage] })
      );
      return { previous };
    },
    onError: (_err, _stage, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(["/api/app/dashboard/dismissed-journey-stages"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/dashboard/dismissed-journey-stages"] });
    },
  });

  if (!guide) return null;
  const visibleActions = guide.actions.filter(action => isCardVisible(action.href));
  if (visibleActions.length === 0) return null;

  const isDismissed = dismissedQuery.data?.dismissedStages?.includes(journeyStage) ?? false;
  if (isDismissed) return null;

  return (
    <div
      className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 border border-blue-200 dark:border-blue-900/60 rounded-xl p-4"
      data-testid={`panel-journey-guide-${journeyStage}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-white dark:bg-blue-950/70 border border-blue-200 dark:border-blue-900/60 flex items-center justify-center shrink-0">
          <Compass className="h-5 w-5 text-brand dark:text-blue-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-blue-700 dark:text-blue-300 uppercase tracking-wide" data-testid="text-journey-stage-label">
            Your stage: {JOURNEY_STAGE_LABELS[journeyStage]}
          </p>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mt-1" data-testid="text-journey-headline">
            {guide.headline}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1" data-testid="text-journey-description">
            {guide.description}
          </p>
          <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
            <div className="flex flex-wrap gap-2">
            {visibleActions.map((action) => (
                <Link key={action.href} href={action.href}>
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-white dark:bg-blue-950/60 border-blue-200 dark:border-blue-800 text-brand dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-900/60"
                    data-testid={`button-journey-action-${action.href.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
                  >
                    {action.label}
                    <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
              ))}
            </div>
            <button
              onClick={() => dismissMutation.mutate(journeyStage)}
              disabled={dismissMutation.isPending}
              className="text-xs text-blue-500 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-200 shrink-0 ml-auto"
              data-testid="button-dismiss-journey-guide"
            >
              Dismiss this help box
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppDashboard() {
  return (
    <AppLayout>
      <DashboardContent />
    </AppLayout>
  );
}

// Access-dependent hooks must run below AppLayout's PermissionsProvider.
// Reading them in the page wrapper gets the context's permanent loading default.
function DashboardContent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  usePageTitle("Dashboard");

  const push = usePushSubscription();

  async function patchAllPushPrefsOn() {
    try {
      // Always fetch authoritative prefs so we never overwrite email/sms choices
      // based on stale or missing cache data.
      const profile = await queryClient.fetchQuery<{ assignmentNotificationPrefs?: unknown }>({
        queryKey: ["/api/app/profile"],
        staleTime: 0,
      });
      const resolved = resolveAssignmentNotificationPrefs(profile?.assignmentNotificationPrefs);
      const allPushOn = Object.fromEntries(
        ASSIGNMENT_NOTIFICATION_TYPES.map((t) => [t, { ...resolved[t], push: true }]),
      );
      await apiRequest("PATCH", "/api/app/profile", { assignmentNotificationPrefs: allPushOn });
      queryClient.invalidateQueries({ queryKey: ["/api/app/profile"] });
    } catch {
      /* best-effort — prefs default to push=true for new users anyway */
    }
    // Close the loop for the user: confirm push is on. Fires once because the
    // hook only invokes onAccepted during the gated first-visit accept window.
    toast({
      title: "Push notifications enabled",
      description: "You'll be notified for mentions and assignments.",
    });
  }

  const { showBanner: showPushBanner, acceptBanner: acceptPushBanner, dismissBanner: dismissPushBanner } =
    useFirstVisitPushPrompt(push, patchAllPushPrefsOn);

  const userQuery = useQuery<{ user: { id: number; selectedPlan?: string; journeyStage?: JourneyStage | null; totpEnabled?: boolean }; effectivePlan?: string; activeMembership?: { ownerUserId: number } | null }>({ queryKey: ["/api/app/auth/me"] });
  const isCardVisible = useHomeCardVisibility();
  const profileQuery = useQuery<{ excludeInactiveRoomsFromOccupancy: boolean }>({ queryKey: ["/api/app/profile"] });
  const propertiesQuery = useQuery<Property[]>({ queryKey: ["/api/app/properties"] });
  const tasksQuery = useQuery<MaintenanceTask[]>({ queryKey: ["/api/app/tasks"] });
  const roomsQuery = useQuery<Array<SchemaRoom & { propertyName: string; propertyAddress: string | null; propertyStatus: string }>>({ queryKey: ["/api/app/rooms"] });
  const connectionsQuery = useQuery<SeamConnection[]>({ queryKey: ["/api/app/devices/connections"] });
  const membersListQuery = useQuery<Array<{ id: number; status: string }>>({ queryKey: ["/api/app/devices/members"] });
  const vendorsQuery = useQuery<Vendor[]>({ queryKey: ["/api/app/operations/vendors"] });
  const expensesQuery = useQuery<{ category: string; amount: string; date: string; propertyId?: number | null }[]>({ queryKey: ["/api/app/expenses"] });
  const dashboardIncomeQuery = useQuery<{ source: string; amount: string; date: string; notes?: string | null; propertyId?: number | null }[]>({
    queryKey: ["/api/app/income"],
    queryFn: async () => {
      const res = await fetch("/api/app/income");
      if (!res.ok) throw new Error("Failed to load income");
      return res.json();
    },
  });
  const dashboardCatSettingsQuery = useQuery<{ customCategories?: { label: string; value: string; isBalanceSheet?: boolean }[] }>({
    queryKey: ["/api/app/expense-category-settings"],
  });

  const previewTier = useMemo(() => {
    if (typeof window === "undefined") return null;
    const p = new URLSearchParams(window.location.search).get("previewTier");
    return p && p.trim() ? p.trim() : null;
  }, []);
  const isPreview = !!previewTier;

  const dashboardPrefsQuery = useQuery<{ panelOrder: string[]; panelSizes?: Record<string, PanelSize> | null; hiddenPanels?: string[] | null; source?: string } | null>({
    queryKey: ["/api/app/dashboard-preferences"],
    enabled: !isPreview,
  });

  const tierDefaultsQuery = useQuery<Array<{ tier: string; panelOrder: string[]; panelSizes?: Record<string, PanelSize> | null; hiddenPanels?: string[] | null }>>({
    queryKey: ["/api/app/admin/dashboard-tier-defaults"],
    enabled: isPreview,
  });

  const [groupFilter, setGroupFilterState] = useState<string>(() => getGroupFilter("dashboard"));
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  function handleGroupFilterChange(value: string) {
    setGroupFilterState(value);
    setGroupFilter("dashboard", value);
  }

  const [panelOrder, setPanelOrder] = useState<string[]>(DEFAULT_PANEL_ORDER);
  const [pendingOrder, setPendingOrder] = useState<string[]>(DEFAULT_PANEL_ORDER);
  const [panelSizes, setPanelSizes] = useState<Record<string, PanelSize>>({});
  const [pendingSizes, setPendingSizes] = useState<Record<string, PanelSize>>({});
  const [hiddenPanels, setHiddenPanels] = useState<string[]>([]);
  const [pendingHidden, setPendingHidden] = useState<string[]>([]);

  useEffect(() => {
    let data: { panelOrder?: string[]; panelSizes?: Record<string, PanelSize> | null; hiddenPanels?: string[] | null } | null | undefined;
    if (isPreview) {
      const match = (tierDefaultsQuery.data ?? []).find((d) => d.tier === previewTier);
      data = match
        ? { panelOrder: match.panelOrder ?? [], panelSizes: match.panelSizes ?? null, hiddenPanels: match.hiddenPanels ?? null }
        : { panelOrder: DEFAULT_PANEL_ORDER, panelSizes: null, hiddenPanels: null };
    } else {
      data = dashboardPrefsQuery.data;
    }
    if (data?.panelOrder) {
      const saved = data.panelOrder;
      const validOrder = saved.some(id => !DEFAULT_PANEL_ORDER.includes(id)) ? [...DEFAULT_PANEL_ORDER] : [
        ...saved.filter((id) => DEFAULT_PANEL_ORDER.includes(id)),
        ...DEFAULT_PANEL_ORDER.filter((id) => !saved.includes(id)),
      ];
      setPanelOrder(validOrder);
      setPendingOrder(validOrder);
    }
    if (data?.panelSizes) {
      setPanelSizes(data.panelSizes);
      setPendingSizes(data.panelSizes);
    } else {
      setPanelSizes({});
      setPendingSizes({});
    }
    if (data?.hiddenPanels) {
      const validHidden = data.hiddenPanels.filter((id) => DEFAULT_PANEL_ORDER.includes(id));
      setHiddenPanels(validHidden);
      setPendingHidden(validHidden);
    } else {
      setHiddenPanels([]);
      setPendingHidden([]);
    }
  }, [dashboardPrefsQuery.data, tierDefaultsQuery.data, isPreview, previewTier]);

  const saveMutation = useMutation({
    mutationFn: (data: { panelOrder: string[]; panelSizes: Record<string, PanelSize>; hiddenPanels: string[] }) =>
      apiRequest("PUT", "/api/app/dashboard-preferences", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/dashboard-preferences"] });
      setPanelOrder(pendingOrder);
      setPanelSizes(pendingSizes);
      setHiddenPanels(pendingHidden);
      setIsEditMode(false);
      setIsPreviewMode(false);
    },
  });

  const resetMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: () => apiRequest("POST", "/api/app/dashboard-preferences/reset", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/dashboard-preferences"] });
      setIsEditMode(false);
      setIsPreviewMode(false);
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setPendingOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }

  function handleEnterEditMode() {
    setPendingOrder(panelOrder);
    setPendingSizes({ ...panelSizes });
    setPendingHidden([...hiddenPanels]);
    setIsEditMode(true);
    setIsPreviewMode(false);
  }

  function handleCancel() {
    setPendingOrder(panelOrder);
    setPendingSizes({ ...panelSizes });
    setPendingHidden([...hiddenPanels]);
    setIsEditMode(false);
    setIsPreviewMode(false);
  }

  function handleSave() {
    saveMutation.mutate({ panelOrder: pendingOrder, panelSizes: pendingSizes, hiddenPanels: pendingHidden });
  }

  function handleResetToDefault() {
    resetMutation.mutate();
  }

  function handleSizeChange(panelId: string, size: PanelSize) {
    setPendingSizes((prev) => {
      const next = { ...prev };
      if (size === "default") {
        delete next[panelId];
      } else {
        next[panelId] = size;
      }
      return next;
    });
  }

  function handleHidePanel(panelId: string) {
    setPendingHidden((prev) => (prev.includes(panelId) ? prev : [...prev, panelId]));
  }

  function handleUnhidePanel(panelId: string) {
    setPendingHidden((prev) => prev.filter((id) => id !== panelId));
  }

  const allProperties = propertiesQuery.data || [];
  const properties = useMemo(() => {
    if (groupFilter === "all") return allProperties;
    return allProperties.filter(p => (p.propertyGroup || "") === groupFilter);
  }, [allProperties, groupFilter]);
  const activeProperties = useMemo(() => properties.filter((p) => p.status === "active"), [properties]);
  const allTasks = tasksQuery.data || [];
  const rooms = roomsQuery.data || [];
  const connections = connectionsQuery.data || [];
  const vendors = vendorsQuery.data || [];

  const groupPropertyIds = useMemo(() => new Set(properties.map((p) => p.id)), [properties]);
  const activePropertyIds = useMemo(() => new Set(activeProperties.map((p) => p.id)), [activeProperties]);

  const excludeInactive = profileQuery.data?.excludeInactiveRoomsFromOccupancy === true;

  const portfolioOccupancy = useMemo(() => {
    const activeRooms = rooms.filter(
      (r) => activePropertyIds.has(r.propertyId) && r.roomType !== "bathroom" && !r.shadowOfRoomId,
    );
    return computeOccupancy(activeRooms, excludeInactive);
  }, [rooms, activePropertyIds, excludeInactive]);

  const totalRooms = useMemo(
    () =>
      rooms.filter(
        (r) => activePropertyIds.has(r.propertyId) && r.roomType !== "bathroom" && !r.shadowOfRoomId,
      ).length,
    [rooms, activePropertyIds],
  );
  const totalActiveMembers = useMemo(
    () => (membersListQuery.data || []).filter((m) => m.status === "active").length,
    [membersListQuery.data],
  );
  const vacantRooms = portfolioOccupancy.vacantRooms;
  const occupancyPct = totalRooms === 0 && portfolioOccupancy.inactiveRooms === 0 ? 100 : portfolioOccupancy.occupancyPct;
  const totalInactiveRooms = portfolioOccupancy.inactiveRooms;

  const filteredTasks = useMemo(() => {
    if (groupFilter === "all") return allTasks;
    return allTasks.filter(t => t.propertyId != null && groupPropertyIds.has(t.propertyId));
  }, [allTasks, groupFilter, groupPropertyIds]);

  const openTasks = useMemo(() => filteredTasks.filter((t) => t.status !== "complete"), [filteredTasks]);

  // Only surface the Open Tasks card when something is actually overdue.
  const hasAnyOverdueTask = useMemo(() => hasOverdueTask(openTasks), [openTasks]);

  const occupancyMetricState = occupancyState(occupancyPct);

  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthKey = format(lastMonth, "yyyy-MM");
  const lastMonthProfit = useMemo(() => {

    // Scope expenses: exclude split parents (mirrors P&L filteredExpenses), then filter by group.
    const rawExpenses = expensesQuery.data || [];
    const splitParentIds = new Set(
      rawExpenses
        .filter((e) => (e as any).splitParentId != null)
        .map((e) => (e as any).splitParentId as number),
    );
    const filteredExpenses = (groupFilter === "all"
      ? rawExpenses
      : rawExpenses.filter((e) => e.propertyId != null && groupPropertyIds.has(e.propertyId))
    ).filter((e) => !splitParentIds.has((e as any).id));

    // Scope income: exclude split parents, filter by group. Treat a non-array
    // payload (error body, unexpected shape) as no income rather than crashing
    // the whole dashboard on `.filter`.
    const rawIncome = Array.isArray(dashboardIncomeQuery.data) ? dashboardIncomeQuery.data : [];
    const incomeSplitParentIds = new Set(
      rawIncome
        .filter((r) => (r as any).splitParentId != null)
        .map((r) => (r as any).splitParentId as number),
    );
    const filteredIncome = (groupFilter === "all"
      ? rawIncome
      : rawIncome.filter((r) => r.propertyId != null && groupPropertyIds.has(r.propertyId))
    ).filter((r) => !incomeSplitParentIds.has((r as any).id));

    // Delegate to the shared authoritative Profit formula — same logic the P&L
    // page uses for its per-month Profit cells.
    return computePnlProfitForMonth(
      filteredExpenses,
      filteredIncome,
      dashboardCatSettingsQuery.data,
      lastMonthKey,
    );
  }, [expensesQuery.data, dashboardIncomeQuery.data, dashboardCatSettingsQuery.data, groupFilter, groupPropertyIds, lastMonthKey]);

  const groupPropertyIdsOrNull = groupFilter !== "all" ? groupPropertyIds : null;

  const panelComponents: Record<string, React.ReactNode> = {
    connections: <ConnectionsPanel />,
    maintenance: <MaintenancePanel tasks={filteredTasks} properties={properties} />,
    approvals: <PendingApprovalsPanel vendors={vendors} properties={properties} activePropertyIds={groupPropertyIdsOrNull} />,
    buyBoxReports: <BuyBoxReportsPanel />,
  };

  const effectiveEditMode = isEditMode && !isPreviewMode;
  const rawActiveOrder = isEditMode ? pendingOrder : panelOrder;
  const activeHidden = isEditMode ? pendingHidden : hiddenPanels;
  // Filter out any panel ids that no longer have a component registered
  // (e.g. retired panels still present in a user's saved dashboard-preferences)
  // AND any panels the user has hidden.
  const isPanelAvailable = (id: string) => id in panelComponents && !!HOME_PANEL_HREFS[id] && isCardVisible(HOME_PANEL_HREFS[id]);
  const activeOrder = rawActiveOrder.filter((id) => isPanelAvailable(id) && !activeHidden.includes(id));
  const activeSizes = isEditMode ? pendingSizes : panelSizes;

  // In edit mode, show which panels are hidden so user can add them back
  const hiddenPanelIds = effectiveEditMode
    ? pendingHidden.filter(isPanelAvailable)
    : [];

  return (
      <div className="p-6 space-y-6" data-testid="page-dashboard">
        <TwoFactorNudgeBanner
          userId={userQuery.data?.user?.id}
          totpEnabled={userQuery.data?.user?.totpEnabled}
        />
        {showPushBanner && (
          <div
            className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3"
            data-testid="banner-push-prompt"
          >
            <Bell className="h-5 w-5 text-brand shrink-0" />
            <div className="flex-1 text-sm text-blue-900">
              <span className="font-medium">Stay on top of assignments.</span>{" "}
              Enable browser notifications to get push alerts when you're @-mentioned or something is assigned to you.
            </div>
            <Button
              size="sm"
              className="bg-brand hover:bg-brand-hover text-white shrink-0"
              onClick={() => void acceptPushBanner()}
              data-testid="button-push-prompt-enable"
            >
              Enable
            </Button>
            <button
              type="button"
              className="text-blue-400 hover:text-blue-600 shrink-0"
              onClick={dismissPushBanner}
              aria-label="Dismiss push notification prompt"
              data-testid="button-push-prompt-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {(() => {
          const effectivePlan = (userQuery.data?.effectivePlan || userQuery.data?.user?.selectedPlan || 'Host') as AccountType;
          const isTeamMember = !!userQuery.data?.activeMembership;
          if (effectivePlan === 'Host' && !isTeamMember) {
            const activeCount = activeProperties.length;
            const roomCount = totalRooms;
            const atPropertyCap = activeCount >= HOST_MAX_ACTIVE_PROPERTIES;
            const atRoomCap = roomCount >= HOST_MAX_ROOMS_PER_PROPERTY;
            if (atPropertyCap || atRoomCap) {
              return (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="banner-host-cap">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800">
                      You're using {activeCount}/{HOST_MAX_ACTIVE_PROPERTIES} active {HOST_MAX_ACTIVE_PROPERTIES === 1 ? 'property' : 'properties'} and {roomCount}/{HOST_MAX_ROOMS_PER_PROPERTY} rooms on the Host plan.
                      {' '}Upgrade to Growth for unlimited properties and rooms.
                    </p>
                  </div>
                  <Link href="/app/account/billing">
                    <Button size="sm" className="bg-brand hover:bg-brand-hover text-white shrink-0" data-testid="button-upgrade-growth">
                      Upgrade to Growth
                    </Button>
                  </Link>
                </div>
              );
            }
          }
          return null;
        })()}
        {userQuery.data?.user?.journeyStage && JOURNEY_GUIDES[userQuery.data.user.journeyStage as JourneyStage] && (
          <JourneyStageGuide journeyStage={userQuery.data.user.journeyStage as JourneyStage} isCardVisible={isCardVisible} />
        )}
        <div className="flex flex-wrap items-start justify-between gap-y-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-tour="dashboard-heading">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-0.5">Your property management overview</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <PropertyGroupFilter
              properties={allProperties}
              value={groupFilter}
              onChange={handleGroupFilterChange}
            />
            {isPreview ? (
              <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-800" data-testid="badge-preview-mode">
                Previewing {previewTier} tier (read-only)
              </Badge>
            ) : isEditMode ? (
              <>
                {hiddenPanelIds.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid="button-hidden-cards-tray"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Hidden ({hiddenPanelIds.length})
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="end">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">Hidden Cards</p>
                      <div className="space-y-1 mt-1">
                        {hiddenPanelIds.map((id) => (
                          <div key={id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-gray-50">
                            <span className="text-sm text-gray-700">{PANEL_LABELS[id] ?? id}</span>
                            <button
                              type="button"
                              onClick={() => handleUnhidePanel(id)}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                              data-testid={`button-unhide-panel-${id}`}
                            >
                              <PlusCircle className="h-3.5 w-3.5" />
                              Add back
                            </button>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                <Button
                  variant={isPreviewMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIsPreviewMode((v) => !v)}
                  disabled={saveMutation.isPending || resetMutation.isPending}
                  data-testid="button-toggle-preview"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  {isPreviewMode ? "Back to Edit" : "Preview"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  disabled={saveMutation.isPending || resetMutation.isPending}
                  data-testid="button-cancel-layout"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetToDefault}
                  disabled={saveMutation.isPending || resetMutation.isPending || isPreviewMode}
                  data-testid="button-reset-layout"
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  {resetMutation.isPending ? "Resetting..." : "Reset to Default"}
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saveMutation.isPending || resetMutation.isPending}
                  data-testid="button-save-layout"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {saveMutation.isPending ? "Saving..." : "Save Layout"}
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEnterEditMode}
                data-testid="button-edit-layout"
              >
                <LayoutGrid className="h-4 w-4 mr-1" />
                Edit Layout
              </Button>
            )}
            <div className="flex items-center gap-2">
              <span data-tour="dashboard-explainer"><ExplainerVideoButton pageKey="dashboard" /></span>
              <GuideButton pageKey="dashboard" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="dashboard" />
        </div>

        <DashboardMetricsGrid
          isCardVisible={isCardVisible}
          activePropertiesCount={activeProperties.length}
          totalRooms={totalRooms}
          occupancyPct={occupancyPct}
          occupancyState={occupancyMetricState}
          excludeInactive={excludeInactive}
          totalInactiveRooms={totalInactiveRooms}
          hasOverdueTask={hasAnyOverdueTask}
          openTasksCount={openTasks.length}
          lastMonthProfit={lastMonthProfit}
          profitMonthLabel={format(lastMonth, "MMMM yyyy")}
          totalActiveMembers={totalActiveMembers}
        />

        {["/app/members/booking-requests", "/app/messages", "/app/devices/smart-locks", "/app/devices/thermostats", "/app/marketing/third-party-listings", "/app/supplies"].some(isCardVisible) && (
          <section className="space-y-3" aria-label="Needs Action" data-testid="dashboard-needs-action">
            <h2 className="text-sm font-semibold text-gray-900">Needs Action</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
              {isCardVisible("/app/members/booking-requests") && <BookingRequestsMetricCard />}
              {isCardVisible("/app/messages") && <DraftMessagesMetricCard />}
              {isCardVisible("/app/messages") && <AwaitingRepliesMetricCard activePropertyIds={groupPropertyIdsOrNull} />}
              {isCardVisible("/app/devices/smart-locks") && <LockHealthPanel connections={connections} metric
                connectionState={connectionsQuery.isError ? "error" : connectionsQuery.isLoading ? "loading" : "ready"} />}
              {isCardVisible("/app/devices/thermostats") && <ThermostatIssuesMetricCard />}
              {isCardVisible("/app/marketing/third-party-listings") && <ListingRemindersPanel activePropertyIds={groupPropertyIdsOrNull} metric />}
              {isCardVisible("/app/supplies") && <SupplyRequestsMetricCard />}
            </div>
          </section>
        )}

        {/* Unified Team Inbox — full-width, first surface below the KPIs and
            not part of the customizable widget grid. Capped to ~50vh with its
            own scroll body so it never pushes the rest of the dashboard down. */}
        <TeamInboxPanel />

        {effectiveEditMode && (
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700" data-testid="banner-edit-help">
            <LayoutGrid className="h-4 w-4 flex-shrink-0" />
            <span>Drag handles to reorder panels. Click the size icon to cycle through sizes. Click the <EyeOff className="h-3.5 w-3.5 inline mx-0.5" /> icon to hide a card. Use <strong>Hidden (n)</strong> to bring cards back. Click <strong>Preview</strong> to see your saved layout, or <strong>Save Layout</strong> when done.</span>
          </div>
        )}
        {isEditMode && isPreviewMode && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="banner-preview-mode">
            <Eye className="h-4 w-4 flex-shrink-0" />
            <span>Preview mode — this is how your dashboard will look after saving. Hidden panels are removed and your new order/sizes are applied. Click <strong>Back to Edit</strong> to keep tweaking, or <strong>Save Layout</strong> to commit.</span>
          </div>
        )}

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={activeOrder} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-card-grid">
              {activeOrder.map((panelId) => (
                <SortableWidget
                  key={panelId}
                  id={panelId}
                  isEditMode={effectiveEditMode}
                  size={activeSizes[panelId] || "default"}
                  onSizeChange={handleSizeChange}
                  onHide={handleHidePanel}
                >
                  {panelComponents[panelId]}
                </SortableWidget>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
  );
}
